// src/features/FeatureSelectionUiProvider.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { IModelApp, type IModelConnection } from "@itwin/core-frontend";
import {
  StagePanelLocation,
  StagePanelSection,
  useActiveIModelConnection,
} from "@itwin/appui-react";

import { FeatureFilterPanel } from "./FeatureFilterPanel";
import { MonitoringAdminWidget, shouldShowMonitoringAdminWidget } from "./MonitoringAdminWidget";
import type { FeatureNodeId } from "./featureTreeData";

import {
  utilityFeatureTypes,
  type UtilityFeatureType,
} from "../domain/utilities/utilityFeatureTypes";
import { useTrimbleConnect } from "../trimble/useTrimbleConnect";
import { TrimbleFolderPicker } from "../components/TrimbleFolderPicker";
import {
  getBackendBase,
  getProjectRootFolderId,
  uploadFileToFolder,
} from "../trimble/coreFiles";
import {
  fetchExportJob,
  submitExportJob,
  type ExportJobSelectedFeature,
  type ExportJobSummary,
} from "../services/jobs/exportJobs";

import {
  query3dFeatureElementsWithRepresentations,
  query2dGraphicsForFeatureDefinition,
  queryElectricalDefinitionNameDiagnostics,
  queryElements2dByDefinitionName,
  queryDefinitionNamesLikeFromModel,
} from "../iTwin/queries/electricalQueries";
import {
  buildLightingCurvesForElement,
  buildLightingCurvesGeometryOnlyForElement,
  buildLightingForElement,
} from "../iTwin/builders/lightingBuilder";
import {
  buildConduitCurvesGeometryOnlyForElement,
  buildConduitCurvesForElement,
  buildConduitForElement,
} from "../iTwin/builders/conduitBuilder";
import { exportElectricalToIfcBlob } from "../iTwin/export/ifc/ifcExporter";
import type { IfcCurvePrimitive } from "../domain/utilities/CurvePrimitives";
import {
  FIELD_INPUT_AS_BUILT_X_PROPERTY,
  FIELD_INPUT_AS_BUILT_Y_PROPERTY,
  FIELD_INPUT_AS_BUILT_Z_PROPERTY,
  FIELD_INPUT_FLAG_DESIGN_PROPERTY,
  FIELD_INPUT_NOTES_PROPERTY,
  normalizeIfcObjectId,
  patchIfcWithFieldInput,
  parseFieldInputValues,
  type FieldInputFormState,
  type FieldInputValues,
} from "../trimble/fieldInputIfc";
import {
  queryNiceConnectAspectsForElement,
  resolveAspectPropertyName,
  type NiceConnectAspectRow,
} from "../services/niceConnectAspect";

const ELECTRICAL_NODE_FEATURES = utilityFeatureTypes.filter(
  (feature) =>
    feature.path.topology === "Node" &&
    feature.path.nodeKind === "ElectricalNode" &&
    (feature.path.group === "Lighting" || feature.path.group === "Signal")
);

const ELECTRICAL_NODE_FEATURE_BY_ID = new Map<FeatureNodeId, UtilityFeatureType>(
  ELECTRICAL_NODE_FEATURES.map((feature) => [feature.id, feature])
);

const LINK_ELECTRICAL_FEATURES = utilityFeatureTypes.filter(
  (feature) =>
    feature.path.topology === "Link" &&
    feature.path.nodeKind === "ElectricalSegment" &&
    (feature.path.group === "Lighting" || feature.path.group === "Signal")
);

const LINK_ELECTRICAL_FEATURE_BY_ID = new Map<FeatureNodeId, UtilityFeatureType>(
  LINK_ELECTRICAL_FEATURES.map((feature) => [feature.id, feature])
);

// Related graphics for electrical nodes are often symbolic 2D cell graphics.
// Export these as true IFC curves via GeometryStream, not tessellated meshes.
const EXPORT_RELATED_GRAPHICS_AS_SEPARATE_IFC_ELEMENTS = true;
const NICECONNECT_ASPECT_CLASS =
  "DgnCustomItemTypes_DOT_DigitalAssetMetadata.NiceConnectElementAspect";
const FIELD_INPUT_STORAGE_KEY = "niceconnect.fieldInputDraft";

function getTrimblePropertyRawValue(
  object: { properties?: Array<{ properties?: Array<{ name?: string; value?: unknown }> }> },
  propertyName: string
): unknown {
  const target = propertyName.trim().toLowerCase();
  const sets = [...(object.properties ?? [])].reverse();
  for (const set of sets) {
    const props = [...(set.properties ?? [])].reverse();
    for (const prop of props) {
      if (String(prop.name ?? "").trim().toLowerCase() !== target) continue;
      return prop.value;
    }
  }
  return undefined;
}

function getTrimblePropertyValue(
  object: { properties?: Array<{ properties?: Array<{ name?: string; value?: unknown }> }> },
  propertyName: string
): string {
  const value = getTrimblePropertyRawValue(object, propertyName);
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function normalizeMatchToken(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function collectTrimbleMatchHints(
  object: { product?: { name?: string }; properties?: Array<{ properties?: Array<{ name?: string; value?: unknown }> }> }
): { entityIdHints: number[]; guidHints: string[]; labelHints: string[] } {
  const entityIdHints = new Set<number>();
  const guidHints = new Set<string>();
  const labelHints = new Set<string>();

  const productName = String(object.product?.name ?? "").trim();
  if (productName) labelHints.add(productName);

  for (const set of object.properties ?? []) {
    for (const prop of set.properties ?? []) {
      const propName = String(prop.name ?? "").trim().toLowerCase();
      const propValue = String(prop.value ?? "").trim();
      if (!propValue) continue;

      if (/(guid|globalid|federation)/i.test(propName)) {
        guidHints.add(propValue.toLowerCase());
      }

      for (const match of propValue.matchAll(/#(\d+)/g)) {
        const parsed = Number.parseInt(match[1], 10);
        if (Number.isFinite(parsed)) {
          entityIdHints.add(parsed);
        }
      }

      if (/(label|name|tag|title|mark|description|text)/i.test(propName)) {
        labelHints.add(propValue);
      }
    }
  }

  return {
    entityIdHints: [...entityIdHints].filter((value) => Number.isFinite(value)),
    guidHints: [...guidHints].filter(Boolean),
    labelHints: [...labelHints].filter(Boolean),
  };
}

function definitionNameVariants(name: string): string[] {
  const base = (name ?? "").trim();
  if (!base) return [];
  const variants = new Set<string>();
  variants.add(base);
  variants.add(base.replace(/\\\\/g, "\\"));
  variants.add(base.replace(/\\/g, "\\\\"));
  return Array.from(variants).filter((v) => v.length > 0);
}

function conduitDefinitionFallbacks(name: string): string[] {
  const base = (name ?? "").trim();
  if (!base) return [];
  const out = new Set<string>();
  out.add(base);
  out.add(base.replace(/\\Signal\\/g, "\\Lighting\\"));
  out.add(base.replace(/\\Lighting\\/g, "\\Signal\\"));
  out.add(base.replace(/\(Signal\)/g, "(Lighting)"));
  out.add(base.replace(/\(Lighting\)/g, "(Signal)"));
  out.add(base.replace(/\\Signal\\/g, "\\Lighting\\").replace(/\(Signal\)/g, "(Lighting)"));
  out.add(base.replace(/\\Lighting\\/g, "\\Signal\\").replace(/\(Lighting\)/g, "(Signal)"));
  return [...out].filter((v) => v.length > 0);
}

function conduitLabelPatterns(label: string): string[] {
  const raw = (label ?? "").trim();
  if (!raw) return [];
  const base = raw
    .replace(/\(Signal\)/gi, "")
    .replace(/\(Lighting\)/gi, "")
    .trim();
  const out = new Set<string>();
  out.add(`%${raw.replace(/'/g, "''")}%`);
  if (base) out.add(`%${base.replace(/'/g, "''")}%`);
  out.add("%Non-Metallic Conduit%");
  return [...out];
}

function electricalNodeDefinitionLeafName(definitionName: string): string {
  const parts = String(definitionName ?? "")
    .split("\\")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return parts.length > 0 ? parts[parts.length - 1] : "";
}

function electricalNodeLabelPatterns(label: string, definitionName: string): string[] {
  const rawLabel = String(label ?? "").trim();
  const leafName = electricalNodeDefinitionLeafName(definitionName);
  const out = new Set<string>();
  if (rawLabel) out.add(`%${rawLabel.replace(/'/g, "''")}%`);
  if (leafName) out.add(`%${leafName.replace(/'/g, "''")}%`);
  return [...out].filter((pattern) => pattern.length > 2);
}

function rebaseCurvesToGroupOrigin(
  curves: IfcCurvePrimitive[],
  fromOrigin: { x: number; y: number; z: number },
  toOrigin: { x: number; y: number; z: number }
): IfcCurvePrimitive[] {
  const dx = (fromOrigin?.x ?? 0) - (toOrigin?.x ?? 0);
  const dy = (fromOrigin?.y ?? 0) - (toOrigin?.y ?? 0);
  const dz = (fromOrigin?.z ?? 0) - (toOrigin?.z ?? 0);

  const shiftPoint = ([x, y, z]: [number, number, number]): [number, number, number] => [
    x + dx,
    y + dy,
    z + dz,
  ];

  return curves.map((curve) => {
    if (curve.kind === "line") {
      return {
        kind: "line",
        start: shiftPoint(curve.start),
        end: shiftPoint(curve.end),
        color: curve.color,
      };
    }
    if (curve.kind === "polyline") {
      return {
        kind: "polyline",
        points: curve.points.map(shiftPoint),
        color: curve.color,
      };
    }
    return {
      kind: "arc",
      center: shiftPoint(curve.center),
      vector0: curve.vector0,
      vector90: curve.vector90,
      start: shiftPoint(curve.start),
      end: shiftPoint(curve.end),
      isCircle: curve.isCircle,
      isFullCircle: curve.isFullCircle,
      color: curve.color,
    };
  });
}

function curveLocalZStats(
  curve: IfcCurvePrimitive
): { minZ: number; maxZ: number; avgZ: number } | null {
  let zValues: number[] = [];

  if (curve.kind === "line") {
    zValues = [curve.start[2], curve.end[2]];
  } else if (curve.kind === "polyline") {
    if (curve.points.length === 0) return null;
    zValues = curve.points.map((p) => p[2]);
  } else {
    zValues = [curve.center[2], curve.start[2], curve.end[2]];
  }

  if (zValues.length === 0) return null;

  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  let sum = 0;
  for (const z of zValues) {
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
    sum += z;
  }

  return { minZ, maxZ, avgZ: sum / zValues.length };
}

type ConduitCurvePlanePartition = {
  planCurves: IfcCurvePrimitive[];
  spatialCurves: IfcCurvePrimitive[];
  selectedPlaneZ?: number;
  changed: boolean;
  confident: boolean;
};

function partitionConduitCurvesByNearZeroPlane(
  curves: IfcCurvePrimitive[]
): ConduitCurvePlanePartition {
  if (curves.length < 2) {
    return {
      planCurves: [],
      spatialCurves: curves,
      changed: false,
      confident: false,
    };
  }

  // Local coordinates are used for extracted curves; prefer the dominant flat plane
  // near z=0 and only apply when we have a clear match.
  const flatSpanTolM = 0.02; // 2 cm
  const bucketTolM = 0.02;
  const nearZeroTolM = 0.10; // 10 cm

  type Bucket = { z: number; indexes: number[] };
  const buckets: Bucket[] = [];

  for (let i = 0; i < curves.length; i++) {
    const stats = curveLocalZStats(curves[i]);
    if (!stats) continue;
    if ((stats.maxZ - stats.minZ) > flatSpanTolM) continue;

    const z = stats.avgZ;
    let bucket = buckets.find((b) => Math.abs(b.z - z) <= bucketTolM);
    if (!bucket) {
      bucket = { z, indexes: [] };
      buckets.push(bucket);
    }
    bucket.indexes.push(i);
  }

  if (buckets.length === 0) {
    return {
      planCurves: [],
      spatialCurves: curves,
      changed: false,
      confident: false,
    };
  }

  const nearZeroBuckets = buckets.filter((b) => Math.abs(b.z) <= nearZeroTolM);
  if (nearZeroBuckets.length === 0) {
    return {
      planCurves: [],
      spatialCurves: curves,
      changed: false,
      confident: false,
    };
  }

  let selected = nearZeroBuckets[0];
  for (const bucket of nearZeroBuckets.slice(1)) {
    if (bucket.indexes.length > selected.indexes.length) {
      selected = bucket;
      continue;
    }
    if (bucket.indexes.length === selected.indexes.length && Math.abs(bucket.z) < Math.abs(selected.z)) {
      selected = bucket;
    }
  }

  if (selected.indexes.length < 2) {
    return {
      planCurves: [],
      spatialCurves: curves,
      changed: false,
      confident: false,
    };
  }

  const keep = new Set<number>(selected.indexes);
  const planCurves = curves.filter((_, index) => keep.has(index));
  const spatialCurves = curves.filter((_, index) => !keep.has(index));

  // Safety: avoid trimming to a tiny fragment unless the match is reasonably strong.
  if (planCurves.length < Math.max(2, Math.floor(curves.length * 0.2))) {
    return {
      planCurves: [],
      spatialCurves: curves,
      changed: false,
      confident: false,
    };
  }

  return {
    planCurves,
    spatialCurves,
    selectedPlaneZ: selected.z,
    changed: planCurves.length !== curves.length,
    confident: true,
  };
}

function filterConduitCurvesToFlatNearZero(curves: IfcCurvePrimitive[]): IfcCurvePrimitive[] {
  const flatSpanTolM = 0.02; // 2 cm
  const nearZeroTolM = 0.10; // 10 cm

  const out: IfcCurvePrimitive[] = [];
  for (const curve of curves) {
    const stats = curveLocalZStats(curve);
    if (!stats) continue;
    if ((stats.maxZ - stats.minZ) > flatSpanTolM) continue;
    if (Math.abs(stats.avgZ) > nearZeroTolM) continue;
    out.push(curve);
  }
  return out;
}

type PendingIfc = {
  blob: Blob;
  filename: string;
  count: number;
};

type PendingBackgroundExportJob = {
  filename: string;
  selectedIds: string[];
  selectedFeatures: ExportJobSelectedFeature[];
  options: {
    include2d: boolean;
    include3d: boolean;
  };
};

type NiceConnectDebugUploadArgs = {
  folderId?: string;
  projectId?: string;
  filename?: string;
  text?: string;
};

type NiceConnectDebugArtifactUploadArgs = {
  folderId?: string;
  projectId?: string;
  filename?: string;
  contentType?: string;
};

type IfcBuildPhase = "retrieving" | "writing" | "finalizing";

type IfcBuildStatus = {
  phase: IfcBuildPhase;
  startedAtMs: number;
  retrieveStartedAtMs: number;
  retrieveCompletedAtMs?: number;
  writeStartedAtMs?: number;
  writeCompletedAtMs?: number;
};

type LastIfcBuildTiming = {
  recordedAtMs: number;
  totalMs: number;
  retrieveMs: number;
  writeMs: number;
  findIdsMs: number;
  buildElementsMs: number;
  basePropsMs: number;
  queryMetadataMs: number;
  extractGeometryMs: number;
  lightingCount: number;
  conduitCount: number;
};

function isTruthyFlag(value: string | null | undefined): boolean {
  return /^(1|true|yes|on)$/i.test(String(value ?? "").trim());
}

function isAsyncExportJobsEnabled(): boolean {
  return true;
}

function isExportDebugPanelsEnabled(): boolean {
  if (typeof window !== "undefined") {
    try {
      const fromSessionStorage = window.sessionStorage.getItem("niceconnect.showExportDebug");
      if (fromSessionStorage !== null) {
        return isTruthyFlag(fromSessionStorage);
      }
    } catch {
      // ignore
    }

    try {
      const fromLocalStorage = window.localStorage.getItem("niceconnect.showExportDebug");
      if (fromLocalStorage !== null) {
        return isTruthyFlag(fromLocalStorage);
      }
    } catch {
      // ignore
    }
  }

  return isTruthyFlag(process.env.REACT_APP_SHOW_EXPORT_DEBUG);
}

function tryDecodeJwtPayload(tokenOrAuthorization: string): Record<string, unknown> | null {
  const rawToken = String(tokenOrAuthorization ?? "").trim().replace(/^Bearer\s+/i, "");
  const parts = rawToken.split(".");
  if (parts.length < 2) return null;

  const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
  try {
    return JSON.parse(window.atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function normalizeNotificationEmailCandidate(value: unknown): string {
  const normalized = String(value ?? "").trim();
  if (!normalized) return "";
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized) ? normalized : "";
}

function resolveBackgroundNotificationEmail(authorization: string): string {
  if (typeof window !== "undefined") {
    try {
      const sessionValue = window.sessionStorage.getItem("niceconnect.notificationEmail");
      const sessionEmail = normalizeNotificationEmailCandidate(sessionValue);
      if (sessionEmail) return sessionEmail;
    } catch {
      // ignore
    }

    try {
      const localValue = window.localStorage.getItem("niceconnect.notificationEmail");
      const localEmail = normalizeNotificationEmailCandidate(localValue);
      if (localEmail) return localEmail;
    } catch {
      // ignore
    }
  }

  const claims = tryDecodeJwtPayload(authorization);
  return (
    normalizeNotificationEmailCandidate(claims?.email) ||
    normalizeNotificationEmailCandidate(claims?.preferred_username) ||
    normalizeNotificationEmailCandidate(claims?.upn) ||
    normalizeNotificationEmailCandidate(claims?.unique_name)
  );
}

function buildDefaultIfcFilename(args: {
  includesConduit: boolean;
  includesElectricalNodes: boolean;
}): string {
  const timestamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0];
  return args.includesConduit && !args.includesElectricalNodes
    ? `conduit_export_${timestamp}.ifc`
    : args.includesConduit
      ? `electrical_conduit_export_${timestamp}.ifc`
      : `electrical_export_${timestamp}.ifc`;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * ConnectProject shapes vary across environments.
 * Keep this as a best-effort shortcut, but DO NOT rely on it as the only method..
 */
function tryGetRootFolderId(project: any): string | null {
  const cand =
    project?.rootFolderId ??
    project?.rootId ??
    project?.filesRootId ??
    project?.fileRootId ??
    project?.foldersRootId;

  if (typeof cand === "string" && cand.trim().length > 0) return cand.trim();
  // Sometimes IDs come back as numbers
  if (typeof cand === "number" && Number.isFinite(cand)) return String(cand);
  return null;
}

function sanitizeIfcFilename(name: string): string {
  const raw = (name ?? "").trim();

  // Replace invalid filename characters and ASCII control chars (0–31)
  const replaced = Array.from(raw)
    .map((ch) => {
      const code = ch.charCodeAt(0);
      if (code >= 0 && code < 32) return "_"; // control chars

      switch (ch) {
        case "<":
        case ">":
        case ":":
        case '"':
        case "/":
        case "\\":
        case "|":
        case "?":
        case "*":
          return "_";
        default:
          return ch;
      }
    })
    .join("")
    .trim();

  if (!replaced) return "export.ifc";
  return replaced.toLowerCase().endsWith(".ifc") ? replaced : `${replaced}.ifc`;
}

function buildClockNowMs(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

function formatDurationMs(ms: number | undefined): string {
  const safe = Math.max(0, Math.round(ms ?? 0));
  const totalSeconds = Math.floor(safe / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const tenths = Math.floor((safe % 1000) / 100);
  return `${minutes}:${String(seconds).padStart(2, "0")}.${tenths}`;
}

function formatDurationWithMs(ms: number | undefined): string {
  const safe = Math.max(0, Math.round(ms ?? 0));
  return `${formatDurationMs(ms)} (${safe} ms)`;
}

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

function parseRetryAfterMs(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.round(value * 1000));
  }

  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const seconds = Number(trimmed);
  if (Number.isFinite(seconds)) return Math.max(0, Math.round(seconds * 1000));

  const asDate = Date.parse(trimmed);
  if (Number.isFinite(asDate)) {
    return Math.max(0, asDate - Date.now());
  }

  return undefined;
}

function collectQuotaErrorText(err: unknown): string {
  const e = err as any;
  const chunks: string[] = [];

  const push = (v: unknown) => {
    if (typeof v === "string" && v.trim()) chunks.push(v);
  };

  push(e?.message);
  push(e?.response?.statusText);
  push(e?.response?.data?.error?.code);
  push(e?.response?.data?.error?.message);
  push(e?.response?.data?.code);
  push(e?.response?.data?.message);
  push(e?.response?.body);

  return chunks.join(" | ");
}

function getQuotaRetryDelayMs(err: unknown): number | undefined {
  const e = err as any;
  const status = Number(e?.response?.status ?? e?.status ?? NaN);
  const retryAfterHeader =
    e?.response?.headers?.["retry-after"] ??
    e?.response?.headers?.["Retry-After"] ??
    e?.headers?.["retry-after"] ??
    e?.headers?.["Retry-After"];

  const retryAfterMs = parseRetryAfterMs(retryAfterHeader);
  const text = collectQuotaErrorText(err);
  const isQuota =
    /QuotaExceeded/i.test(text) ||
    (status === 403 && /quota/i.test(text));

  if (!isQuota) return undefined;

  const replenishedMatch = /replenished in\s+(\d{1,2}):(\d{2})(?::(\d{2}))?/i.exec(text);
  let wait = retryAfterMs;
  if (replenishedMatch) {
    const a = Number(replenishedMatch[1] ?? 0);
    const b = Number(replenishedMatch[2] ?? 0);
    const cRaw = replenishedMatch[3];
    const c = cRaw ? Number(cRaw) : 0;
    const totalSeconds = cRaw ? (a * 3600 + b * 60 + c) : (a * 60 + b);
    if (Number.isFinite(totalSeconds) && totalSeconds >= 0) {
      wait = Math.max(wait ?? 0, totalSeconds * 1000);
    }
  }

  return Math.min(Math.max((wait ?? 15000) + 1000, 1000), 180000);
}

async function withQuotaRetry<T>(
  label: string,
  fn: () => Promise<T>,
  maxRetries = 2
): Promise<T> {
  let attempt = 0;

  while (true) {
    try {
      return await fn();
    } catch (err) {
      const wait = getQuotaRetryDelayMs(err);
      if (wait === undefined || attempt >= maxRetries) throw err;

      attempt += 1;
      console.warn(
        `[IFC Export] iTwin quota exceeded during ${label}. Retrying in ${Math.ceil(wait / 1000)}s (attempt ${attempt}/${maxRetries}).`
      );
      await waitMs(wait);
    }
  }
}

type ResolvedTrimbleFeatureSelection = {
  sourceElementIds: string[];
  matchedDefinitionNames: string[];
};

type FeatureSelectionSyncState = {
  summary: string | null;
  lines: string[];
};

type TrimbleSelectionAttempt = {
  label: string;
  propertyName: string;
  values: string[];
};

type TrimbleSelectionMatch = TrimbleSelectionAttempt & {
  matchedCount: number;
  modelCount: number;
};

function uniqueNonEmptyStrings(values: Array<unknown>): string[] {
  const out = new Set<string>();
  for (const value of values) {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) continue;
    out.add(trimmed);
  }
  return [...out];
}

function assignFeatureLabelByElementId(
  index: Map<string, string>,
  elementIds: Iterable<string>,
  featureLabel: unknown
): void {
  const label = String(featureLabel ?? "").trim();
  if (!label) return;

  for (const rawElementId of elementIds) {
    const elementId = String(rawElementId ?? "").trim();
    if (!elementId || index.has(elementId)) continue;
    index.set(elementId, label);
  }
}

function getSelectedFeatureBuckets(selectedLeafIds: FeatureNodeId[]): {
  selectedElectricalNodeFeatures: UtilityFeatureType[];
  selectedConduitFeatures: UtilityFeatureType[];
} {
  return {
    selectedElectricalNodeFeatures: selectedLeafIds
      .map((id) => ELECTRICAL_NODE_FEATURE_BY_ID.get(id))
      .filter((feature): feature is UtilityFeatureType => Boolean(feature)),
    selectedConduitFeatures: selectedLeafIds
      .map((id) => LINK_ELECTRICAL_FEATURE_BY_ID.get(id))
      .filter((feature): feature is UtilityFeatureType => Boolean(feature)),
  };
}

async function resolveElectricalNodeFeatureSelection(
  iModelConnection: IModelConnection,
  feature: UtilityFeatureType
): Promise<ResolvedTrimbleFeatureSelection> {
  const sourceElementIds = new Set<string>();
  const matchedDefinitionNames = new Set<string>();
  let candidateDefinitionNamesLoaded = false;
  const candidateDefinitionNames = new Set<string>();

  const loadCandidateDefinitionNames = async (): Promise<string[]> => {
    if (!candidateDefinitionNamesLoaded) {
      candidateDefinitionNamesLoaded = true;
      for (const pattern of electricalNodeLabelPatterns(feature.label, feature.definitionName)) {
        const candidates = await queryDefinitionNamesLikeFromModel(iModelConnection, pattern, 20);
        for (const candidateName of candidates) candidateDefinitionNames.add(candidateName);
      }
    }
    return [...candidateDefinitionNames];
  };

  const collect3dForDefinition = async (
    definitionName: string
  ): Promise<{ ids: string[]; matchedDefinitionName?: string }> => {
    const ids = new Set<string>();
    let matchedDefinitionName: string | undefined;
    for (const variant of definitionNameVariants(definitionName)) {
      const linked3d = await query3dFeatureElementsWithRepresentations(iModelConnection, variant);
      for (const id of linked3d.primaryElementIds) ids.add(id);
      if (ids.size > 0) {
        matchedDefinitionName = variant;
        break;
      }
    }
    return { ids: [...ids], matchedDefinitionName };
  };

  const collect2dForDefinition = async (
    definitionName: string
  ): Promise<{ ids: string[]; matchedDefinitionName?: string }> => {
    const ids = new Set<string>();
    let matchedDefinitionName: string | undefined;
    for (const variant of definitionNameVariants(definitionName)) {
      const linked2d = await query2dGraphicsForFeatureDefinition(iModelConnection, variant);
      for (const id of linked2d.relatedGraphicIds) ids.add(id);
      const direct2d = await queryElements2dByDefinitionName(iModelConnection, variant);
      for (const id of direct2d) ids.add(id);
      if (ids.size > 0) {
        matchedDefinitionName = variant;
        break;
      }
    }
    return { ids: [...ids], matchedDefinitionName };
  };

  let ids3d = await collect3dForDefinition(feature.definitionName);
  if (ids3d.ids.length === 0) {
    for (const candidateName of await loadCandidateDefinitionNames()) {
      ids3d = await collect3dForDefinition(candidateName);
      if (ids3d.ids.length > 0) break;
    }
  }
  for (const id of ids3d.ids) sourceElementIds.add(id);
  if (ids3d.matchedDefinitionName) matchedDefinitionNames.add(ids3d.matchedDefinitionName);

  let ids2d = await collect2dForDefinition(feature.definitionName);
  if (ids2d.ids.length === 0) {
    for (const candidateName of await loadCandidateDefinitionNames()) {
      ids2d = await collect2dForDefinition(candidateName);
      if (ids2d.ids.length > 0) break;
    }
  }
  for (const id of ids2d.ids) sourceElementIds.add(id);
  if (ids2d.matchedDefinitionName) matchedDefinitionNames.add(ids2d.matchedDefinitionName);

  return {
    sourceElementIds: [...sourceElementIds],
    matchedDefinitionNames: [...matchedDefinitionNames],
  };
}

function hasResolvedConduitIds(result: {
  ids3d: string[];
  ids3dCurveOnly: string[];
  ids2d: string[];
}): boolean {
  return result.ids3d.length > 0 || result.ids3dCurveOnly.length > 0 || result.ids2d.length > 0;
}

async function resolveConduitFeatureSelection(
  iModelConnection: IModelConnection,
  feature: UtilityFeatureType
): Promise<ResolvedTrimbleFeatureSelection> {
  const queryConduitIdsForDefinition = async (
    definitionName: string
  ): Promise<{
    ids3d: string[];
    ids3dCurveOnly: string[];
    ids2d: string[];
    matchedDefinitionName?: string;
  }> => {
    let ids3d: string[] = [];
    let ids3dCurveOnly: string[] = [];
    let ids2d: string[] = [];
    let matchedDefinitionName: string | undefined;

    for (const candidateDef of conduitDefinitionFallbacks(definitionName)) {
      for (const variant of definitionNameVariants(candidateDef)) {
        if (ids3d.length === 0) {
          const linked3d = await query3dFeatureElementsWithRepresentations(iModelConnection, variant);
          ids3d = linked3d.primaryElementIds;
        }

        if (ids2d.length === 0 || ids3dCurveOnly.length === 0) {
          const linked2d = await query2dGraphicsForFeatureDefinition(iModelConnection, variant);
          if (linked2d.relatedGraphic3dIds.length > 0) {
            ids3dCurveOnly = Array.from(new Set([...ids3dCurveOnly, ...linked2d.relatedGraphic3dIds]));
          }
          if (ids2d.length === 0) {
            ids2d =
              linked2d.relatedGraphic2dIds.length > 0
                ? linked2d.relatedGraphic2dIds
                : await queryElements2dByDefinitionName(iModelConnection, variant);
          }
        }

        if (ids3d.length > 0 || ids2d.length > 0 || ids3dCurveOnly.length > 0) {
          matchedDefinitionName = variant;
          break;
        }
      }

      if (matchedDefinitionName) break;
    }

    return {
      ids3d: uniqueNonEmptyStrings(ids3d),
      ids3dCurveOnly: uniqueNonEmptyStrings(ids3dCurveOnly),
      ids2d: uniqueNonEmptyStrings(ids2d),
      matchedDefinitionName,
    };
  };

  let resolved = await queryConduitIdsForDefinition(feature.definitionName);

  if (!hasResolvedConduitIds(resolved)) {
    let altDefinitionName: string | null = null;
    if (feature.definitionName.startsWith("Link\\")) {
      altDefinitionName = feature.definitionName.replace(/^Link\\/, "Conduit\\");
    } else if (feature.definitionName.startsWith("Conduit\\")) {
      altDefinitionName = feature.definitionName.replace(/^Conduit\\/, "Link\\");
    }

    if (altDefinitionName) {
      const viaAlt = await queryConduitIdsForDefinition(altDefinitionName);
      if (hasResolvedConduitIds(viaAlt)) {
        resolved = viaAlt;
      }
    }
  }

  if (!hasResolvedConduitIds(resolved)) {
    const candidateDefinitionNames = new Set<string>();
    for (const pattern of conduitLabelPatterns(feature.label)) {
      const candidates = await queryDefinitionNamesLikeFromModel(iModelConnection, pattern, 20);
      for (const candidateName of candidates) candidateDefinitionNames.add(candidateName);
    }

    for (const candidateName of candidateDefinitionNames) {
      const viaCandidate = await queryConduitIdsForDefinition(candidateName);
      if (hasResolvedConduitIds(viaCandidate)) {
        resolved = viaCandidate;
        break;
      }
    }
  }

  return {
    sourceElementIds: uniqueNonEmptyStrings([
      ...resolved.ids3d,
      ...resolved.ids3dCurveOnly,
      ...resolved.ids2d,
    ]),
    matchedDefinitionNames: uniqueNonEmptyStrings([resolved.matchedDefinitionName]),
  };
}

async function resolveSelectedTrimbleFeatureSelection(
  iModelConnection: IModelConnection,
  selectedLeafIds: FeatureNodeId[]
): Promise<ResolvedTrimbleFeatureSelection> {
  const { selectedElectricalNodeFeatures, selectedConduitFeatures } =
    getSelectedFeatureBuckets(selectedLeafIds);

  if (selectedElectricalNodeFeatures.length === 0 && selectedConduitFeatures.length === 0) {
    return { sourceElementIds: [], matchedDefinitionNames: [] };
  }

  const sourceElementIds = new Set<string>();
  const matchedDefinitionNames = new Set<string>();

  const electricalResults = await Promise.all(
    selectedElectricalNodeFeatures.map((feature) =>
      resolveElectricalNodeFeatureSelection(iModelConnection, feature)
    )
  );
  for (const result of electricalResults) {
    for (const id of result.sourceElementIds) sourceElementIds.add(id);
    for (const definitionName of result.matchedDefinitionNames) {
      matchedDefinitionNames.add(definitionName);
    }
  }

  const conduitResults = await Promise.all(
    selectedConduitFeatures.map((feature) => resolveConduitFeatureSelection(iModelConnection, feature))
  );
  for (const result of conduitResults) {
    for (const id of result.sourceElementIds) sourceElementIds.add(id);
    for (const definitionName of result.matchedDefinitionNames) {
      matchedDefinitionNames.add(definitionName);
    }
  }

  return {
    sourceElementIds: [...sourceElementIds],
    matchedDefinitionNames: [...matchedDefinitionNames],
  };
}

function buildTrimblePropertySelector(propertyName: string, values: string[]) {
  const uniqueValues = uniqueNonEmptyStrings(values);
  return {
    parameter: {
      properties: {
        [propertyName]: uniqueValues.length === 1 ? uniqueValues[0] : uniqueValues,
      },
    },
  };
}

function useITwinFeatureSelectionSync(
  iModelConnection: IModelConnection | undefined
): FeatureSelectionSyncState & { handleSelectionChange: (selectedLeafIds: FeatureNodeId[]) => Promise<void> } {
  const [summary, setSummary] = useState<string | null>(null);
  const [lines, setLines] = useState<string[]>([]);
  const requestIdRef = useRef(0);

  useEffect(() => {
    requestIdRef.current += 1;
    setSummary(null);
    setLines([]);
  }, [iModelConnection]);

  const handleSelectionChange = useCallback(
    async (selectedLeafIds: FeatureNodeId[]) => {
      const requestId = ++requestIdRef.current;
      const isStale = () => requestId !== requestIdRef.current;
      const updateState = (nextSummary: string | null, nextLines: string[] = []) => {
        if (isStale()) return;
        setSummary(nextSummary);
        setLines(nextLines);
      };

      if (selectedLeafIds.length === 0) {
        iModelConnection?.selectionSet.emptyAll();
        updateState(null, []);
        return;
      }

      if (!iModelConnection) {
        updateState("No active iModel connection.");
        return;
      }

      updateState("Selecting iTwin elements...");

      try {
        const resolved = await resolveSelectedTrimbleFeatureSelection(iModelConnection, selectedLeafIds);
        if (isStale()) return;

        const sourceElementIds = uniqueNonEmptyStrings(resolved.sourceElementIds);
        const matchedDefinitionNames = uniqueNonEmptyStrings(resolved.matchedDefinitionNames);

        if (sourceElementIds.length === 0) {
          iModelConnection.selectionSet.emptyAll();
          updateState("No matching iTwin feature elements were found for the current tree selection.");
          return;
        }

        iModelConnection.selectionSet.replace(sourceElementIds);
        updateState(
          `Selected ${sourceElementIds.length} iTwin element(s).`,
          [`Resolved ${matchedDefinitionNames.length} feature definition value(s).`]
        );
      } catch (err) {
        console.error("[iTwin Sync] Feature-selection sync failed:", err);
        updateState((err as Error).message ?? "iTwin selection sync failed.");
      }
    },
    [iModelConnection]
  );

  return { summary, lines, handleSelectionChange };
}

async function clearTrimbleViewerSelection(trimbleViewer: any): Promise<void> {
  await trimbleViewer.setSelection({ selected: true }, "remove");
}

async function tryTrimbleSelectionAttempts(
  trimbleViewer: any,
  attempts: TrimbleSelectionAttempt[],
  isStale: () => boolean
): Promise<TrimbleSelectionMatch | null> {
  for (const attempt of attempts) {
    const values = uniqueNonEmptyStrings(attempt.values);
    if (values.length === 0) continue;

    const selector = buildTrimblePropertySelector(attempt.propertyName, values);

    try {
      if (isStale()) return null;

      const modelObjects = await trimbleViewer.getObjects(selector);
      if (isStale()) return null;

      const matchingEntries = (modelObjects ?? []).filter(
        (entry: any) => (entry?.objects?.length ?? 0) > 0
      );
      const matchedCount = matchingEntries.reduce(
        (total: number, entry: any) => total + (entry?.objects?.length ?? 0),
        0
      );
      if (matchedCount === 0) continue;

      const modelCount = new Set(
        matchingEntries
          .map((entry: any) => String(entry?.modelId ?? "").trim())
          .filter((value: string) => value.length > 0)
      ).size;

      await trimbleViewer.setSelection(selector, "set");
      if (isStale()) return null;

      return {
        ...attempt,
        values,
        matchedCount,
        modelCount,
      };
    } catch (err) {
      console.warn(
        `[Trimble Sync] Failed selection attempt '${attempt.label}' on property '${attempt.propertyName}'.`,
        err
      );
    }
  }

  return null;
}

function useTrimbleFeatureSelectionSync(
  iModelConnection: IModelConnection | undefined,
  trimbleViewer: any
): FeatureSelectionSyncState & { handleSelectionChange: (selectedLeafIds: FeatureNodeId[]) => Promise<void> } {
  const [summary, setSummary] = useState<string | null>(null);
  const [lines, setLines] = useState<string[]>([]);
  const requestIdRef = useRef(0);

  useEffect(() => {
    requestIdRef.current += 1;
    setSummary(null);
    setLines([]);
  }, [iModelConnection, trimbleViewer]);

  const handleSelectionChange = useCallback(
    async (selectedLeafIds: FeatureNodeId[]) => {
      const requestId = ++requestIdRef.current;
      const isStale = () => requestId !== requestIdRef.current;
      const updateState = (nextSummary: string | null, nextLines: string[] = []) => {
        if (isStale()) return;
        setSummary(nextSummary);
        setLines(nextLines);
      };

      if (selectedLeafIds.length === 0) {
        if (trimbleViewer) {
          try {
            await clearTrimbleViewerSelection(trimbleViewer);
          } catch (err) {
            console.warn("[Trimble Sync] Failed to clear Trimble selection.", err);
          }
        }
        updateState(null, []);
        return;
      }

      if (!iModelConnection) {
        updateState("No active iModel connection.");
        return;
      }

      if (!trimbleViewer) {
        updateState("Trimble viewer API is not available in this session.");
        return;
      }

      updateState("Syncing Trimble selection...");

      try {
        const resolved = await resolveSelectedTrimbleFeatureSelection(iModelConnection, selectedLeafIds);
        if (isStale()) return;

        const sourceElementIds = uniqueNonEmptyStrings(resolved.sourceElementIds);
        const matchedDefinitionNames = uniqueNonEmptyStrings(resolved.matchedDefinitionNames);

        if (sourceElementIds.length === 0 && matchedDefinitionNames.length === 0) {
          await clearTrimbleViewerSelection(trimbleViewer);
          updateState("No matching electrical or signal feature elements were found for the current tree selection.");
          return;
        }

        let match = await tryTrimbleSelectionAttempts(
          trimbleViewer,
          [
            {
              label: "source element id",
              propertyName: "NiceConnect Azure Export.Source Element Id",
              values: sourceElementIds,
            },
            {
              label: "source element id",
              propertyName: "NiceConnect Export.Source Element Id",
              values: sourceElementIds,
            },
          ],
          isStale
        );

        if (!match) {
          match = await tryTrimbleSelectionAttempts(
            trimbleViewer,
            [
              {
                label: "feature definition",
                propertyName: "Feature.Feature Definition",
                values: matchedDefinitionNames,
              },
            ],
            isStale
          );
        }

        if (!match) {
          await clearTrimbleViewerSelection(trimbleViewer);
          updateState(
            `Resolved ${sourceElementIds.length} source element id(s), but no loaded Trimble IFC objects matched the current feature selection.`,
            matchedDefinitionNames.length > 0
              ? [`Tried ${matchedDefinitionNames.length} feature definition value(s) as a fallback.`]
              : []
          );
          return;
        }

        updateState(
          `Selected ${match.matchedCount} Trimble object(s) across ${match.modelCount} loaded model(s).`,
          [
            `Matched by ${match.propertyName}.`,
            `Resolved ${sourceElementIds.length} source element id(s) across ${matchedDefinitionNames.length} feature definition value(s).`,
          ]
        );
      } catch (err) {
        console.error("[Trimble Sync] Feature-selection sync failed:", err);
        updateState((err as Error).message ?? "Trimble selection sync failed.");
      }
    },
    [iModelConnection, trimbleViewer]
  );

  return { summary, lines, handleSelectionChange };
}

const FeatureSelectionWidget: React.FC = () => {
  const iModelConnection = useActiveIModelConnection();

  const trimble = useTrimbleConnect();
  const { project, status: trimbleStatus, ensureAccessToken } = trimble;
  const iTwinSelectionSync = useITwinFeatureSelectionSync(iModelConnection);

  // Folder picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingIfc, setPendingIfc] = useState<PendingIfc | null>(null);
  const [pendingBackgroundExportJob, setPendingBackgroundExportJob] =
    useState<PendingBackgroundExportJob | null>(null);
  const [pickerAccessToken, setPickerAccessToken] = useState<string | null>(null);
  const [rootFolderId, setRootFolderId] = useState<string | null>(null);

  const [isBuildingIfc, setIsBuildingIfc] = useState(false);
  const [ifcBuildStatus, setIfcBuildStatus] = useState<IfcBuildStatus | null>(null);
  const [ifcBuildNowMs, setIfcBuildNowMs] = useState<number>(buildClockNowMs());
  const [lastIfcBuildTiming, setLastIfcBuildTiming] = useState<LastIfcBuildTiming | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmittingBackgroundJob, setIsSubmittingBackgroundJob] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [activeBackgroundExportJob, setActiveBackgroundExportJob] =
    useState<ExportJobSummary | null>(null);
  const backgroundExportJobsEnabled = isAsyncExportJobsEnabled();
  const showExportDebugPanels = isExportDebugPanelsEnabled();

  useEffect(() => {
    if (!uploadSuccess) return;
    const timer = window.setTimeout(() => setUploadSuccess(null), 3500);
    return () => window.clearTimeout(timer);
  }, [uploadSuccess]);

  useEffect(() => {
    if (!isBuildingIfc) return;
    setIfcBuildNowMs(buildClockNowMs());
    const timer = window.setInterval(() => setIfcBuildNowMs(buildClockNowMs()), 250);
    return () => window.clearInterval(timer);
  }, [isBuildingIfc]);

  // Best-effort: if the ConnectProject already includes root folder info, capture it.
  useEffect(() => {
    const best = tryGetRootFolderId(project as any);
    if (best && !rootFolderId) setRootFolderId(best);
  }, [project, rootFolderId]);

  useEffect(() => {
    const debugWindow = window as typeof window & {
      niceconnectDebugUploadText?: (args?: NiceConnectDebugUploadArgs) => Promise<any>;
      niceconnectDebugUploadLatestIfcArtifact?: (
        args?: NiceConnectDebugArtifactUploadArgs
      ) => Promise<any>;
    };

    const debugUploadText = async (args: NiceConnectDebugUploadArgs = {}) => {
      const token = await ensureAccessToken();
      if (!token) {
        throw new Error("Trimble access token was unavailable.");
      }

      const folderId =
        String(args.folderId ?? "").trim() ||
        String(activeBackgroundExportJob?.trimble?.folderId ?? "").trim();
      if (!folderId) {
        throw new Error("Missing folderId. Pass one into window.niceconnectDebugUploadText({ folderId: \"...\" }).");
      }

      const projectId =
        String(args.projectId ?? "").trim() || String(project?.id ?? "").trim();
      const backendBase = getBackendBase();
      if (!backendBase) {
        throw new Error("NiceConnect backend base was unavailable.");
      }

      const response = await fetch(`${backendBase.replace(/\/$/, "")}/trimble/debug-upload-text`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          folderId,
          projectId,
          filename: String(args.filename ?? "").trim() || "niceconnect-debug-upload.txt",
          text:
            String(args.text ?? "").trim() ||
            "NiceConnect Azure debug upload test from embedded Trimble app.",
        }),
      });

      const raw = await response.text();
      let parsed: unknown = raw;
      try {
        parsed = raw ? JSON.parse(raw) : {};
      } catch {
        // keep raw text
      }

      if (!response.ok) {
        const detail =
          typeof parsed === "string" ? parsed : JSON.stringify(parsed);
        throw new Error(`Debug Trimble text upload failed (${response.status}): ${detail}`);
      }

      console.info("[NiceConnect Debug] Trimble text upload result:", parsed);
      return parsed;
    };

    const debugUploadLatestIfcArtifact = async (
      args: NiceConnectDebugArtifactUploadArgs = {}
    ) => {
      const token = await ensureAccessToken();
      if (!token) {
        throw new Error("Trimble access token was unavailable.");
      }

      const latestJob = activeBackgroundExportJob;
      if (!latestJob?.jobId) {
        throw new Error("No active background export job is available for artifact re-upload.");
      }

      const ifcArtifact = Array.isArray(latestJob.result?.artifacts)
        ? latestJob.result.artifacts.find((artifact) => String(artifact?.kind ?? "").trim() === "ifc")
        : null;
      if (!ifcArtifact?.artifactId) {
        throw new Error("The active background export job does not have an IFC artifact to retry.");
      }

      const folderId =
        String(args.folderId ?? "").trim() ||
        String(latestJob.trimble?.folderId ?? "").trim();
      if (!folderId) {
        throw new Error(
          "Missing folderId. Pass one into window.niceconnectDebugUploadLatestIfcArtifact({ folderId: \"...\" })."
        );
      }

      const projectId =
        String(args.projectId ?? "").trim() ||
        String(latestJob.trimble?.projectId ?? "").trim() ||
        String(project?.id ?? "").trim();
      const backendBase = getBackendBase();
      if (!backendBase) {
        throw new Error("NiceConnect backend base was unavailable.");
      }

      const response = await fetch(
        `${backendBase.replace(/\/$/, "")}/jobs/${encodeURIComponent(latestJob.jobId)}/artifacts/${encodeURIComponent(ifcArtifact.artifactId)}/trimble-upload`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            folderId,
            projectId,
            filename: String(args.filename ?? "").trim() || ifcArtifact.filename,
            contentType: String(args.contentType ?? "").trim() || ifcArtifact.contentType,
          }),
        }
      );

      const raw = await response.text();
      let parsed: unknown = raw;
      try {
        parsed = raw ? JSON.parse(raw) : {};
      } catch {
        // keep raw text
      }

      if (!response.ok) {
        const detail = typeof parsed === "string" ? parsed : JSON.stringify(parsed);
        throw new Error(`Debug IFC artifact upload failed (${response.status}): ${detail}`);
      }

      console.info("[NiceConnect Debug] Trimble IFC artifact upload result:", parsed);
      return parsed;
    };

    debugWindow.niceconnectDebugUploadText = debugUploadText;
    debugWindow.niceconnectDebugUploadLatestIfcArtifact = debugUploadLatestIfcArtifact;
    return () => {
      if (debugWindow.niceconnectDebugUploadText === debugUploadText) {
        delete debugWindow.niceconnectDebugUploadText;
      }
      if (
        debugWindow.niceconnectDebugUploadLatestIfcArtifact === debugUploadLatestIfcArtifact
      ) {
        delete debugWindow.niceconnectDebugUploadLatestIfcArtifact;
      }
    };
  }, [activeBackgroundExportJob, ensureAccessToken, project?.id]);

  useEffect(() => {
    if (!activeBackgroundExportJob) return;
    if (
      activeBackgroundExportJob.status === "completed" ||
      activeBackgroundExportJob.status === "failed" ||
      activeBackgroundExportJob.status === "canceled"
    ) {
      return;
    }

    let cancelled = false;
    const pollTimer = window.setTimeout(async () => {
      try {
        const nextJob = await fetchExportJob(activeBackgroundExportJob.jobId);
        if (cancelled) return;

        setActiveBackgroundExportJob(nextJob);

        if (
          nextJob.status === "completed" &&
          activeBackgroundExportJob.status !== "completed"
        ) {
          setUploadError(null);
          setUploadSuccess("File export complete.");
        } else if (
          nextJob.status === "failed" &&
          activeBackgroundExportJob.status !== "failed"
        ) {
          setUploadSuccess(null);
          setUploadError(
            nextJob.error
              ? `Background import job failed: ${nextJob.error}`
              : `Background import job ${nextJob.jobId.slice(0, 8)} failed.`
          );
        }
      } catch (err) {
        if (cancelled) return;
        console.error("[IFC Export] Background job polling failed:", err);
        setUploadSuccess(null);
        setUploadError(`Background import job polling failed: ${(err as Error).message ?? String(err)}`);
      }
    }, 1500);

    return () => {
      cancelled = true;
      window.clearTimeout(pollTimer);
    };
  }, [activeBackgroundExportJob]);

  const resolveRootFolderId = useCallback(
    async (token: string): Promise<string | null> => {
      if (rootFolderId) return rootFolderId;

      const best = tryGetRootFolderId(project as any);
      if (best) {
        setRootFolderId(best);
        return best;
      }

      if (!project?.id) return null;

      const fetched = await getProjectRootFolderId(project.id, token);
      setRootFolderId(fetched);
      return fetched;
    },
    [project, rootFolderId]
  );

  /**
   * IFC Export flow:
   * 1) Build IFC blob
   * 2) Acquire token (user gesture context: button click)
   * 3) Resolve root folder id
   * 4) Prompt user for filename (optional but satisfies “name” requirement)
   * 5) Open folder picker to choose placement
   * 6) Upload after folder is selected
   */
  const handleIfcExportClick = useCallback(
    async (
      selectedLeafIds: FeatureNodeId[],
      options: { include2d: boolean; include3d: boolean } = { include2d: true, include3d: true }
    ) => {
      setUploadError(null);
      setUploadSuccess(null);
      setActiveBackgroundExportJob(null);
      const include2d = options.include2d;
      const include3d = options.include3d;

      if (!include2d && !include3d) {
        console.warn("[IFC Export] At least one of 2D or 3D must be selected.");
        return;
      }

      if (!iModelConnection) {
        console.warn("[IFC Export] No active iModelConnection.");
        return;
      }

      const { selectedElectricalNodeFeatures, selectedConduitFeatures } =
        getSelectedFeatureBuckets(selectedLeafIds);
      const includesElectricalNodes = selectedElectricalNodeFeatures.length > 0;
      const includesConduit = selectedConduitFeatures.length > 0;

      if (!includesElectricalNodes && !includesConduit) {
        return;
      }

      const relevantSelectedIds = Array.from(
        new Set([
          ...selectedElectricalNodeFeatures.map((feature) => feature.id),
          ...selectedConduitFeatures.map((feature) => feature.id),
        ])
      );
      const selectedFeatureDescriptors: ExportJobSelectedFeature[] = [
        ...selectedElectricalNodeFeatures.map((feature) => ({
          id: feature.id,
          label: feature.label,
          definitionName: feature.definitionName,
          featureKind: "electrical-node",
        })),
        ...selectedConduitFeatures.map((feature) => ({
          id: feature.id,
          label: feature.label,
          definitionName: feature.definitionName,
          featureKind: "conduit",
        })),
      ];
      const defaultName = buildDefaultIfcFilename({
        includesConduit,
        includesElectricalNodes,
      });
      const useBackgroundExportJobs =
        backgroundExportJobsEnabled && trimbleStatus === "ready" && Boolean(project?.id);

      if (useBackgroundExportJobs) {
        try {
          setLastIfcBuildTiming(null);
          const token = await ensureAccessToken();
          if (!token) {
            throw new Error("Trimble access token was unavailable for the background import job.");
          }

          const rid = await resolveRootFolderId(token);
          if (!rid) {
            throw new Error("Could not resolve the Trimble root folder for the background import job.");
          }

          const entered = window.prompt("IFC filename:", defaultName);
          if (entered === null) {
            return;
          }

          const finalName = sanitizeIfcFilename(entered);
          setPendingIfc(null);
          setPendingBackgroundExportJob({
            filename: finalName,
            selectedIds: relevantSelectedIds,
            selectedFeatures: selectedFeatureDescriptors,
            options: { include2d, include3d },
          });
          setPickerAccessToken(token);
          setRootFolderId(rid);
          setPickerOpen(true);
          setUploadSuccess("Choose a Trimble folder to start file export.");
        } catch (err) {
          console.error("[IFC Export] Background job preparation failed:", err);
          setUploadError((err as Error).message ?? String(err));
        }
        return;
      }

      const buildStartMs = buildClockNowMs();
      const elementBuildTiming = {
        basePropsMs: 0,
        metadataMs: 0,
        geometryMs: 0,
      };
      let findIdsCompletedAtMs = buildStartMs;
      let buildElementsStartMs = buildStartMs;
      let buildElementsEndMs = buildStartMs;
      setIfcBuildStatus({
        phase: "retrieving",
        startedAtMs: buildStartMs,
        retrieveStartedAtMs: buildStartMs,
      });
      setLastIfcBuildTiming(null);
      setIsBuildingIfc(true);
      try {
        try {
          const diagnostics = await queryElectricalDefinitionNameDiagnostics(iModelConnection);
          const configuredElectricalNodeDefinitions = selectedElectricalNodeFeatures.map(
            (feature) => feature.definitionName
          );
          const matchedConfiguredNodeDiagnostics = diagnostics.filter((entry) =>
            configuredElectricalNodeDefinitions.includes(entry.definitionName)
          );
          const lightingAndSignalDiagnostics = diagnostics.filter(
            (entry) =>
              entry.topology === "Node" &&
              (entry.group === "Lighting" || entry.group === "Signal")
          );

          console.groupCollapsed("[IFC Export] Electrical DefinitionName diagnostics");
          console.info(
            "[IFC Export] Configured electrical node definitions:",
            configuredElectricalNodeDefinitions
          );
          console.info(
            "[IFC Export] Matching configured definitions found in model:",
            matchedConfiguredNodeDiagnostics
          );
          console.info(
            "[IFC Export] All lighting/signal node definitions found in model:",
            lightingAndSignalDiagnostics
          );
          console.info(
            "[IFC Export] All electrical definitions found in model:",
            diagnostics
          );
          console.groupEnd();
        } catch (diagnosticError) {
          console.warn(
            "[IFC Export] Failed to gather electrical DefinitionName diagnostics:",
            diagnosticError
          );
        }

        // 1) Query for lighting element IDs
        const ids: string[] = [];
        const node2dIds: string[] = [];
        const relatedGraphicIdsByLightingPrimary: Record<string, string[]> = {};
        const lightingFeatureLabelsByElementId = new Map<string, string>();
        for (const feature of selectedElectricalNodeFeatures) {
          const collect3dForDefinition = async (definitionName: string) => {
            const outIds = new Set<string>();
            const relatedByPrimary: Record<string, string[]> = {};

            for (const variant of definitionNameVariants(definitionName)) {
              const linked3d = await query3dFeatureElementsWithRepresentations(
                iModelConnection,
                variant
              );
              for (const id of linked3d.primaryElementIds) outIds.add(id);
              for (const [primaryId, relatedIds] of Object.entries(
                linked3d.relatedGraphicIdsByPrimary
              )) {
                const bucket = relatedByPrimary[primaryId] ?? [];
                for (const rid of relatedIds) {
                  if (!bucket.includes(rid)) bucket.push(rid);
                }
                relatedByPrimary[primaryId] = bucket;
              }
              if (outIds.size > 0) break;
            }

            return {
              idsForFeature: [...outIds],
              relatedByPrimary,
            };
          };

          let {
            idsForFeature,
            relatedByPrimary,
          } = await collect3dForDefinition(feature.definitionName);

          if (idsForFeature.length === 0) {
            const candidateSet = new Set<string>();
            for (const pattern of electricalNodeLabelPatterns(
              feature.label,
              feature.definitionName
            )) {
              const candidates = await queryDefinitionNamesLikeFromModel(
                iModelConnection,
                pattern,
                20
              );
              for (const candidateName of candidates) candidateSet.add(candidateName);
            }

            for (const candidateName of candidateSet) {
              const viaCandidate = await collect3dForDefinition(candidateName);
              if (viaCandidate.idsForFeature.length > 0) {
                idsForFeature = viaCandidate.idsForFeature;
                relatedByPrimary = viaCandidate.relatedByPrimary;
                console.warn(
                  `[IFC Export] Electrical node '${feature.label}' matched candidate DefinitionName '${candidateName}'.`
                );
                break;
              }
            }

            if (idsForFeature.length === 0 && candidateSet.size === 0) {
              console.warn(
                `[IFC Export] No candidate DefinitionNames found for electrical node '${feature.label}' using configured DefinitionName '${feature.definitionName}'.`
              );
            }
          }

          for (const [primaryId, relatedIds] of Object.entries(relatedByPrimary)) {
            const bucket = relatedGraphicIdsByLightingPrimary[primaryId] ?? [];
            for (const rid of relatedIds) {
              if (!bucket.includes(rid)) bucket.push(rid);
            }
            relatedGraphicIdsByLightingPrimary[primaryId] = bucket;
          }
          ids.push(...idsForFeature);
          assignFeatureLabelByElementId(lightingFeatureLabelsByElementId, idsForFeature, feature.label);

          if (include2d) {
            const collect2dForDefinition = async (definitionName: string): Promise<string[]> => {
              const out = new Set<string>();
              for (const variant of definitionNameVariants(definitionName)) {
                const linked2d = await query2dGraphicsForFeatureDefinition(
                  iModelConnection,
                  variant
                );
                for (const id of linked2d.relatedGraphicIds) out.add(id);
                const direct2d = await queryElements2dByDefinitionName(iModelConnection, variant);
                for (const id of direct2d) out.add(id);
                if (out.size > 0) break;
              }
              return [...out];
            };

            let ids2dForFeature = await collect2dForDefinition(feature.definitionName);
            if (ids2dForFeature.length === 0) {
              const candidateSet = new Set<string>();
              for (const pattern of electricalNodeLabelPatterns(
                feature.label,
                feature.definitionName
              )) {
                const candidates = await queryDefinitionNamesLikeFromModel(
                  iModelConnection,
                  pattern,
                  20
                );
                for (const candidateName of candidates) candidateSet.add(candidateName);
              }
              for (const candidateName of candidateSet) {
                ids2dForFeature = await collect2dForDefinition(candidateName);
                if (ids2dForFeature.length > 0) {
                  console.warn(
                    `[IFC Export] Electrical node '${feature.label}' 2D graphics matched candidate DefinitionName '${candidateName}'.`
                  );
                  break;
                }
              }
            }
            assignFeatureLabelByElementId(
              lightingFeatureLabelsByElementId,
              ids2dForFeature,
              feature.label
            );
            node2dIds.push(...ids2dForFeature);
          }
        }

        const allLightingIds = Array.from(new Set(ids));
        const allRelatedGraphicLightingIds = Array.from(
          new Set(
            Object.values(relatedGraphicIdsByLightingPrimary).flat()
          )
        ).filter((id) => !allLightingIds.includes(id));
        const allNode2dIds = Array.from(new Set(node2dIds))
          .filter((id) => !allLightingIds.includes(id))
          .filter((id) => !allRelatedGraphicLightingIds.includes(id));

        const lightingIds = include3d ? allLightingIds : [];
        const relatedGraphicLightingIds = include2d ? allRelatedGraphicLightingIds : [];
        const standaloneNode2dIds = include2d ? allNode2dIds : [];

        // 1b) Query for conduit IDs
        const conduit3dIds: string[] = [];
        const conduit3dCurveGraphicIds: string[] = [];
        const conduit2dIds: string[] = [];
        const conduitFeatureLabelsByElementId = new Map<string, string>();
        if (include3d || include2d) {
          for (const feature of selectedConduitFeatures) {
            const queryConduitIdsForDefinition = async (definitionName: string) => {
              let ids3d: string[] = [];
              let ids3dCurveOnly: string[] = [];
              let ids2d: string[] = [];
              for (const candidateDef of conduitDefinitionFallbacks(definitionName)) {
                for (const variant of definitionNameVariants(candidateDef)) {
                  // Conduit "2D" curve export frequently reuses the conduit feature's
                  // own geometric element (often 3D) as the curve source, so gather
                  // 3D feature ids whenever conduit export is enabled.
                  if ((include3d || include2d) && ids3d.length === 0) {
                    const linked3d = await query3dFeatureElementsWithRepresentations(
                      iModelConnection,
                      variant
                    );
                    ids3d = linked3d.primaryElementIds;
                  }
                  if ((include2d || include3d) && (ids2d.length === 0 || ids3dCurveOnly.length === 0)) {
                    const linked2d = await query2dGraphicsForFeatureDefinition(
                      iModelConnection,
                      variant
                    );
                    if (linked2d.relatedGraphic3dIds.length > 0) {
                      ids3dCurveOnly = Array.from(
                        new Set([...ids3dCurveOnly, ...linked2d.relatedGraphic3dIds])
                      );
                      console.info(
                        `[IFC Export] Conduit definition '${variant}' returned ${linked2d.relatedGraphic3dIds.length} 3D related graphic ids on the 2D relationship query; routing them to 3D linework processing.`
                      );
                    }
                    if (include2d && ids2d.length === 0) {
                      ids2d =
                        linked2d.relatedGraphic2dIds.length > 0
                          ? linked2d.relatedGraphic2dIds
                          : await queryElements2dByDefinitionName(iModelConnection, variant);
                    }
                  }
                  if (ids3d.length > 0 || ids2d.length > 0 || ids3dCurveOnly.length > 0) break;
                }
                if (ids3d.length > 0 || ids2d.length > 0 || ids3dCurveOnly.length > 0) break;
              }
              return { ids3d, ids3dCurveOnly, ids2d };
            };

            let {
              ids3d: ids3dForFeature,
              ids3dCurveOnly: ids3dCurveOnlyForFeature,
              ids2d: ids2dForFeature,
            } =
              await queryConduitIdsForDefinition(feature.definitionName);

            if (
              ids3dForFeature.length === 0 &&
              ids3dCurveOnlyForFeature.length === 0 &&
              ids2dForFeature.length === 0
            ) {
              let altDefinitionName: string | null = null;
              if (feature.definitionName.startsWith("Link\\")) {
                altDefinitionName = feature.definitionName.replace(/^Link\\/, "Conduit\\");
              } else if (feature.definitionName.startsWith("Conduit\\")) {
                altDefinitionName = feature.definitionName.replace(/^Conduit\\/, "Link\\");
              }

              if (altDefinitionName) {
                const viaAlt = await queryConduitIdsForDefinition(altDefinitionName);
                ids3dForFeature = viaAlt.ids3d;
                ids3dCurveOnlyForFeature = viaAlt.ids3dCurveOnly;
                ids2dForFeature = viaAlt.ids2d;

                if (
                  ids3dForFeature.length > 0 ||
                  ids3dCurveOnlyForFeature.length > 0 ||
                  ids2dForFeature.length > 0
                ) {
                  console.warn(
                    `[IFC Export] '${feature.label}' matched alt DefinitionName '${altDefinitionName}'.`
                  );
                }
              }
            }

            if (
              ids3dForFeature.length === 0 &&
              ids3dCurveOnlyForFeature.length === 0 &&
              ids2dForFeature.length === 0
            ) {
              const patterns = conduitLabelPatterns(feature.label);
              const candidateSet = new Set<string>();
              for (const pattern of patterns) {
                const candidates = await queryDefinitionNamesLikeFromModel(
                  iModelConnection,
                  pattern,
                  20
                );
                for (const c of candidates) candidateSet.add(c);
              }
              const candidates = [...candidateSet];
              if (candidates.length > 0) {
                console.warn(
                  `[IFC Export] No elements matched DefinitionName '${feature.definitionName}'. Candidates:`,
                  candidates
                );

                // Fallback: try candidate DefinitionNames from the model.
                for (const candidateName of candidates) {
                  const viaCandidate = await queryConduitIdsForDefinition(candidateName);
                  const candidate3d = viaCandidate.ids3d;
                  const candidate3dCurveOnly = viaCandidate.ids3dCurveOnly;
                  const candidate2d = viaCandidate.ids2d;

                  if (
                    candidate3d.length > 0 ||
                    candidate3dCurveOnly.length > 0 ||
                    candidate2d.length > 0
                  ) {
                    ids3dForFeature = candidate3d;
                    ids3dCurveOnlyForFeature = candidate3dCurveOnly;
                    ids2dForFeature = candidate2d;
                    console.warn(
                      `[IFC Export] '${feature.label}' matched candidate DefinitionName '${candidateName}'.`
                    );
                    break;
                  }
                }
              } else {
                console.warn(
                  `[IFC Export] No elements matched DefinitionName '${feature.definitionName}'.`
                );
              }
            }

            assignFeatureLabelByElementId(
              conduitFeatureLabelsByElementId,
              ids3dForFeature,
              feature.label
            );
            assignFeatureLabelByElementId(
              conduitFeatureLabelsByElementId,
              ids3dCurveOnlyForFeature,
              feature.label
            );
            assignFeatureLabelByElementId(
              conduitFeatureLabelsByElementId,
              ids2dForFeature,
              feature.label
            );
            conduit3dIds.push(...ids3dForFeature);
            conduit3dCurveGraphicIds.push(...ids3dCurveOnlyForFeature);
            conduit2dIds.push(...ids2dForFeature);
          }
        }

        const conduitIds3d = Array.from(new Set(conduit3dIds));
        const conduitIds3dCurveOnly = Array.from(new Set(conduit3dCurveGraphicIds))
          .filter((id) => !conduitIds3d.includes(id));
        const conduitIds2d = Array.from(new Set(conduit2dIds));
        const conduitCurveIds = include2d
          ? Array.from(new Set([...conduitIds2d, ...conduitIds3d, ...conduitIds3dCurveOnly]))
          : [];
        const conduit2dIdSet = new Set(conduitIds2d);
        const supplementalConduitCurveIdSet = new Set(
          include2d ? conduitCurveIds.filter((id) => !conduit2dIdSet.has(id)) : []
        );

        if (include2d && conduitCurveIds.length > conduitIds2d.length) {
          console.info(
            `[IFC Export] Conduit 2D curve export supplemented ${conduitCurveIds.length - conduitIds2d.length} IDs from 3D conduit features.`
          );
        }
        if (include3d && conduitIds3dCurveOnly.length > 0) {
          console.info(
            `[IFC Export] Conduit export identified ${conduitIds3dCurveOnly.length} 3D related-graphic ids for curve-only processing.`
          );
        }

        if (includesElectricalNodes && include3d && include2d && lightingIds.length === 0 && relatedGraphicLightingIds.length === 0) {
          console.warn("[IFC Export] No electrical node elements found to export.");
          if (!includesConduit) return;
        }

        if (includesElectricalNodes && include3d && !include2d && lightingIds.length === 0) {
          console.warn("[IFC Export] No electrical node 3D elements found to export.");
          if (!includesConduit) return;
        }

        if (
          includesElectricalNodes &&
          include2d &&
          !include3d &&
          relatedGraphicLightingIds.length === 0 &&
          standaloneNode2dIds.length === 0
        ) {
          console.warn("[IFC Export] No electrical node 2D related graphics found to export.");
          if (!includesConduit) return;
        }

        if (includesElectricalNodes && !include3d && !include2d) {
          console.warn("[IFC Export] No electrical node elements found to export.");
          if (!includesConduit) return;
        }

        findIdsCompletedAtMs = buildClockNowMs();
        buildElementsStartMs = findIdsCompletedAtMs;

        // 2) Build Lighting objects
        const lightings: any[] = [];
        if (include3d) {
          for (const elementId of lightingIds) {
            try {
              const lighting = await withQuotaRetry(
                `build lighting ${elementId}`,
                () => buildLightingForElement(iModelConnection, elementId, elementBuildTiming)
              );
              if (lighting) {
                (lighting as any).__sourceElementId = elementId;
                (lighting as any).__featureLabel = lightingFeatureLabelsByElementId.get(elementId) ?? "";
                (lighting as any).__geometryRole = "3D Mesh";
                (lighting as any).__entityType = "Lighting";
                lightings.push(lighting);
              }
            } catch (err) {
              console.error(`[IFC Export] Failed to build lighting for element ${elementId}:`, err);
            }
          }
        }

        if (include2d && EXPORT_RELATED_GRAPHICS_AS_SEPARATE_IFC_ELEMENTS) {
          // Build one grouped 2D "cell" element per primary lighting, composed
          // of all related graphics linked to that primary.
          for (const [primaryId, relatedIds] of Object.entries(
            relatedGraphicIdsByLightingPrimary
          )) {
            if (!relatedIds.length) continue;

            let groupedLighting: any | undefined;
            const groupedCurves: IfcCurvePrimitive[] = [];

            for (const relatedId of relatedIds) {
              try {
                const useGeometryOnlyBuilder = groupedLighting !== undefined;
                const relatedLighting = await withQuotaRetry(
                  `build related 2D lighting graphic ${relatedId}`,
                  () =>
                    useGeometryOnlyBuilder
                      ? buildLightingCurvesGeometryOnlyForElement(iModelConnection, relatedId, elementBuildTiming)
                      : buildLightingCurvesForElement(iModelConnection, relatedId, elementBuildTiming)
                );
                if (!relatedLighting) continue;

                if (!groupedLighting) groupedLighting = relatedLighting;
                if (Array.isArray((relatedLighting as any).Curves)) {
                  const sourceCurves = (relatedLighting as any).Curves as IfcCurvePrimitive[];
                  const rebased = rebaseCurvesToGroupOrigin(
                    sourceCurves,
                    {
                      x: Number((relatedLighting as any).Origin?.x ?? 0),
                      y: Number((relatedLighting as any).Origin?.y ?? 0),
                      z: Number((relatedLighting as any).Origin?.z ?? 0),
                    },
                    {
                      x: Number((groupedLighting as any).Origin?.x ?? 0),
                      y: Number((groupedLighting as any).Origin?.y ?? 0),
                      z: Number((groupedLighting as any).Origin?.z ?? 0),
                    }
                  );
                  groupedCurves.push(...rebased);
                }
              } catch (err) {
                console.error(
                  `[IFC Export] Failed to build related lighting graphic ${relatedId}:`,
                  err
                );
              }
            }

            if (groupedLighting && groupedCurves.length > 0) {
              (groupedLighting as any).Curves = groupedCurves;
              (groupedLighting as any).Meshes = undefined;
              (groupedLighting as any).__sourceElementId = primaryId;
              (groupedLighting as any).__sourceRelatedElementIds = relatedIds.join("|");
              (groupedLighting as any).__featureLabel =
                lightingFeatureLabelsByElementId.get(primaryId) ?? "";
              (groupedLighting as any).__geometryRole = "2D Curve";
              (groupedLighting as any).__entityType = "Lighting";
              if (groupedLighting.UserLabel) {
                groupedLighting.UserLabel = `${groupedLighting.UserLabel} (2D Cell)`;
              } else {
                groupedLighting.UserLabel = "2D Cell";
              }
              lightings.push(groupedLighting);
            } else if (relatedIds.length > 0) {
              console.warn(
                `[IFC Export] Primary ${primaryId} had ${relatedIds.length} related graphics but no curve primitives were produced.`
              );
            }
          }
        } else if (relatedGraphicLightingIds.length > 0) {
          // 2D export is disabled; ignore related graphics.
        }

        if (include2d) {
          for (const elementId of standaloneNode2dIds) {
            try {
              const node2d = await withQuotaRetry(
                `build standalone 2D lighting ${elementId}`,
                () => buildLightingCurvesForElement(iModelConnection, elementId, elementBuildTiming)
              );
              if (node2d) {
                (node2d as any).__sourceElementId = elementId;
                (node2d as any).__featureLabel = lightingFeatureLabelsByElementId.get(elementId) ?? "";
                (node2d as any).__geometryRole = "2D Curve";
                (node2d as any).__entityType = "Lighting";
                lightings.push(node2d);
              }
            } catch (err) {
              console.error(
                `[IFC Export] Failed to build standalone 2D electrical graphic ${elementId}:`,
                err
              );
            }
          }
        }

        if (includesElectricalNodes && lightings.length === 0) {
          console.warn("[IFC Export] No electrical node objects could be built.");
          if (!includesConduit) return;
        }

        // 2b) Build Conduit objects
        const conduits: any[] = [];
        if (include3d) {
          for (const elementId of conduitIds3d) {
            try {
              const conduit = await withQuotaRetry(
                `build conduit ${elementId}`,
                () => buildConduitForElement(iModelConnection, elementId, elementBuildTiming)
              );
              if (!conduit) continue;
              (conduit as any).__sourceElementId = elementId;
              (conduit as any).__featureLabel = conduitFeatureLabelsByElementId.get(elementId) ?? "";
              (conduit as any).__geometryRole = "3D Mesh";
              (conduit as any).__entityType = "Conduit";

              try {
                const conduitCurves3d = await withQuotaRetry(
                  `build 3D conduit curves ${elementId}`,
                  () =>
                    buildConduitCurvesGeometryOnlyForElement(
                      iModelConnection,
                      elementId,
                      elementBuildTiming
                    )
                );
                const curves = Array.isArray((conduitCurves3d as any)?.Curves)
                  ? ((conduitCurves3d as any).Curves as IfcCurvePrimitive[])
                  : undefined;
                if (curves && curves.length > 0) {
                  const partition = partitionConduitCurvesByNearZeroPlane(curves);
                  const spatialCurves = partition.confident ? partition.spatialCurves : curves;
                  if (spatialCurves.length > 0) {
                    const conduit3dLinework = {
                      ...(conduit as any),
                      Curves: spatialCurves,
                      Meshes: undefined,
                    };
                    conduit3dLinework.UserLabel = conduit3dLinework.UserLabel
                      ? `${conduit3dLinework.UserLabel} (3D Linework)`
                      : "Conduit (3D Linework)";
                    (conduit3dLinework as any).__sourceElementId = elementId;
                    (conduit3dLinework as any).__geometryRole = "3D Linework";
                    (conduit3dLinework as any).__entityType = "Conduit";
                    conduits.push(conduit3dLinework);
                    console.info(
                      partition.confident
                        ? `[IFC Export] 3D conduit kept ${spatialCurves.length}/${curves.length} spatial curves for ${elementId} (plan plane z~${(partition.selectedPlaneZ ?? 0).toFixed(3)}m).`
                        : `[IFC Export] 3D conduit kept all ${curves.length} curves for ${elementId} (no confident near-zero plan split).`
                    );
                  }
                }
              } catch (curveErr) {
                console.warn(
                  `[IFC Export] Failed to build 3D conduit curves for element ${elementId}; exporting meshes only.`,
                  curveErr
                );
              }

              (conduit as any).Curves = undefined;
              (conduit as any).__geometryRole = "3D Mesh";
              conduits.push(conduit);
            } catch (err) {
              console.error(`[IFC Export] Failed to build conduit for element ${elementId}:`, err);
            }
          }

          for (const elementId of conduitIds3dCurveOnly) {
            try {
              const conduit3dCurveOnly = await withQuotaRetry(
                `build 3D conduit curve-only graphic ${elementId}`,
                () =>
                  buildConduitCurvesGeometryOnlyForElement(
                    iModelConnection,
                    elementId,
                    elementBuildTiming
                  )
              );
              if (!conduit3dCurveOnly) continue;

              const curves = Array.isArray((conduit3dCurveOnly as any).Curves)
                ? ((conduit3dCurveOnly as any).Curves as IfcCurvePrimitive[])
                : undefined;
              if (!curves || curves.length === 0) continue;

              const partition = partitionConduitCurvesByNearZeroPlane(curves);
              const spatialCurves = partition.confident ? partition.spatialCurves : curves;
              if (spatialCurves.length === 0) continue;

              (conduit3dCurveOnly as any).Curves = spatialCurves;
              (conduit3dCurveOnly as any).Meshes = undefined;
              (conduit3dCurveOnly as any).UserLabel = (conduit3dCurveOnly as any).UserLabel
                ? `${(conduit3dCurveOnly as any).UserLabel} (3D Linework)`
                : "Conduit (3D Linework)";
              (conduit3dCurveOnly as any).__sourceElementId = elementId;
              (conduit3dCurveOnly as any).__featureLabel =
                conduitFeatureLabelsByElementId.get(elementId) ?? "";
              (conduit3dCurveOnly as any).__geometryRole = "3D Linework";
              (conduit3dCurveOnly as any).__entityType = "Conduit";
              console.info(
                partition.confident
                  ? `[IFC Export] 3D conduit curve-only graphic ${elementId} kept ${spatialCurves.length}/${curves.length} spatial curves (plan plane z~${(partition.selectedPlaneZ ?? 0).toFixed(3)}m).`
                  : `[IFC Export] 3D conduit curve-only graphic ${elementId} kept all ${curves.length} curves (no confident near-zero plan split).`
              );
              conduits.push(conduit3dCurveOnly);
            } catch (err) {
              console.error(
                `[IFC Export] Failed to build 3D conduit curve-only graphic ${elementId}:`,
                err
              );
            }
          }
        }
        if (include2d) {
          for (const elementId of conduitCurveIds) {
            try {
              const conduit2d = await withQuotaRetry(
                `build 2D conduit ${elementId}`,
                () => buildConduitCurvesForElement(iModelConnection, elementId, elementBuildTiming)
              );
              if (conduit2d) {
                (conduit2d as any).__sourceElementId = elementId;
                (conduit2d as any).__featureLabel = conduitFeatureLabelsByElementId.get(elementId) ?? "";
                (conduit2d as any).__geometryRole = "2D Curve";
                (conduit2d as any).__entityType = "Conduit";
              }
              if (conduit2d && supplementalConduitCurveIdSet.has(elementId)) {
                const curves = Array.isArray((conduit2d as any).Curves)
                  ? ((conduit2d as any).Curves as IfcCurvePrimitive[])
                  : undefined;
                if (curves && curves.length > 0) {
                  const partition = partitionConduitCurvesByNearZeroPlane(curves);
                  if (partition.confident && partition.planCurves.length > 0) {
                    (conduit2d as any).Curves = partition.planCurves;
                    console.info(
                      `[IFC Export] 2D conduit z-filter kept ${partition.planCurves.length}/${curves.length} curves for supplemental 3D conduit ${elementId} (plane z~${(partition.selectedPlaneZ ?? 0).toFixed(3)}m).`
                    );
                  } else {
                    const fallbackPlanCurves = filterConduitCurvesToFlatNearZero(curves);
                    (conduit2d as any).Curves = fallbackPlanCurves;
                    if (fallbackPlanCurves.length > 0) {
                      console.info(
                        `[IFC Export] 2D conduit strict fallback kept ${fallbackPlanCurves.length}/${curves.length} flat near-zero curves for supplemental 3D conduit ${elementId}.`
                      );
                    } else {
                      console.info(
                        `[IFC Export] 2D conduit dropped supplemental 3D conduit ${elementId}; no confident plan split and no flat near-zero fallback curves.`
                      );
                    }
                  }
                }
              }
              if (conduit2d && supplementalConduitCurveIdSet.has(elementId)) {
                const outCurves = Array.isArray((conduit2d as any).Curves)
                  ? ((conduit2d as any).Curves as IfcCurvePrimitive[])
                  : [];
                if (outCurves.length === 0) continue;
              }
              if (conduit2d) conduits.push(conduit2d);
            } catch (err) {
              console.error(
                `[IFC Export] Failed to build 2D conduit curves for element ${elementId}:`,
                err
              );
            }
          }
        }

        if (includesConduit && conduits.length === 0 && lightings.length === 0) {
          console.warn("[IFC Export] No conduit objects could be built.");
          return;
        }

        buildElementsEndMs = buildClockNowMs();

        // 3) Create IFC blob
        const writeStartMs = buildClockNowMs();
        setIfcBuildStatus((prev) => {
          if (!prev) {
            return {
              phase: "writing",
              startedAtMs: writeStartMs,
              retrieveStartedAtMs: writeStartMs,
              retrieveCompletedAtMs: writeStartMs,
              writeStartedAtMs: writeStartMs,
            };
          }
          return {
            ...prev,
            phase: "writing",
            retrieveCompletedAtMs: prev.retrieveCompletedAtMs ?? writeStartMs,
            writeStartedAtMs: writeStartMs,
          };
        });
        setIfcBuildNowMs(writeStartMs);
        // Yield one frame so the UI can paint "Writing IFC" before the synchronous
        // IFC serialization blocks the main thread.
        await waitForNextPaint();
        const blob = exportElectricalToIfcBlob(lightings, conduits, defaultName);
        const writeEndMs = buildClockNowMs();
        setIfcBuildStatus((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            phase: "finalizing",
            writeCompletedAtMs: writeEndMs,
          };
        });
        setLastIfcBuildTiming((prev) => {
          void prev;
          const retrieveCompletedAtMs = writeStartMs;
          const retrieveMs = Math.max(0, retrieveCompletedAtMs - buildStartMs);
          const writeMs = Math.max(0, writeEndMs - writeStartMs);
          const totalMs = Math.max(0, writeEndMs - buildStartMs);
          const findIdsMs = Math.max(0, findIdsCompletedAtMs - buildStartMs);
          const buildElementsMs = Math.max(0, buildElementsEndMs - buildElementsStartMs);
          const basePropsMs = Math.max(0, elementBuildTiming.basePropsMs ?? 0);
          const queryMetadataMs = Math.max(0, elementBuildTiming.metadataMs ?? 0);
          const extractGeometryMs = Math.max(0, elementBuildTiming.geometryMs ?? 0);
          console.info("[IFC Export] Timing", {
            findIdsMs: Math.round(findIdsMs),
            buildElementsMs: Math.round(buildElementsMs),
            basePropsMs: Math.round(basePropsMs),
            queryMetadataMs: Math.round(queryMetadataMs),
            extractGeometryMs: Math.round(extractGeometryMs),
            retrieveMs: Math.round(retrieveMs),
            writeMs: Math.round(writeMs),
            totalMs: Math.round(totalMs),
            lightingCount: lightings.length,
            conduitCount: conduits.length,
          });
          return {
            recordedAtMs: Date.now(),
            totalMs,
            retrieveMs,
            writeMs,
            findIdsMs,
            buildElementsMs,
            basePropsMs,
            queryMetadataMs,
            extractGeometryMs,
            lightingCount: lightings.length,
            conduitCount: conduits.length,
          };
        });

        // If not in Trimble (or missing project), fall back to download
        if (trimbleStatus !== "ready" || !project?.id) {
          downloadBlob(blob, defaultName);
          return;
        }

        // 4) Acquire token (avoid using stale state; use return value)
        const token = await ensureAccessToken();
        if (!token) {
          console.warn("[IFC Export] Could not obtain access token. Downloading locally.");
          downloadBlob(blob, defaultName);
          return;
        }

        // 5) Resolve root folder id (required for picker)
        const rid = await resolveRootFolderId(token);
        if (!rid) {
          console.warn("[IFC Export] Could not resolve root folder id. Downloading locally.");
          downloadBlob(blob, defaultName);
          return;
        }

        // 6) Ask user for filename (simple approach; replace with a nicer dialog later if desired)
        const entered = window.prompt("IFC filename:", defaultName);
        if (entered === null) {
          // user cancelled naming step -> do nothing (or download if you prefer)
          return;
        }
        const finalName = sanitizeIfcFilename(entered);

        // 7) Open folder picker to choose placement
        setPendingIfc({
          blob,
          filename: finalName,
          count: lightings.length,
        });
        setPickerAccessToken(token);
        setRootFolderId(rid);
        setPickerOpen(true);
      } catch (err) {
        console.error("[IFC Export] Export failed:", err);
        setUploadError((err as Error).message ?? String(err));
      } finally {
        setIsBuildingIfc(false);
      }
    },
    [
      backgroundExportJobsEnabled,
      iModelConnection,
      trimbleStatus,
      project?.id,
      ensureAccessToken,
      resolveRootFolderId,
    ]
  );

  const handleFolderPickerCancel = useCallback(() => {
    setPickerOpen(false);

    if (pendingBackgroundExportJob) {
      setPendingBackgroundExportJob(null);
    }

    // If user cancels folder selection, download locally so they still get the file
    if (pendingIfc) {
      downloadBlob(pendingIfc.blob, pendingIfc.filename);
      setPendingIfc(null);
    }
  }, [pendingBackgroundExportJob, pendingIfc]);

  const handleFolderPicked = useCallback(
    async (folder: { id: string; name?: string; path?: string }) => {
      if (!pendingIfc && !pendingBackgroundExportJob) return;

      setPickerOpen(false);
      setUploadError(null);

      if (pendingBackgroundExportJob) {
        setIsSubmittingBackgroundJob(true);
        try {
          if (!project?.id) throw new Error("Trimble project missing.");
          if (!iModelConnection?.iTwinId || !iModelConnection?.iModelId) {
            throw new Error("Active iTwin connection is missing for the background import job.");
          }

          const trimbleToken = pickerAccessToken ?? (await ensureAccessToken());
          if (!trimbleToken) {
            throw new Error("Trimble access token missing for the background import job.");
          }

          const token = String((await IModelApp.getAccessToken()) ?? "").trim();
          if (!token) {
            throw new Error("Bentley access token missing for the background import job.");
          }

          const authorization = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
          const notificationEmail = resolveBackgroundNotificationEmail(authorization);
          const job = await submitExportJob({
            authorization,
            trimbleAccessToken: trimbleToken,
            iTwinId: iModelConnection.iTwinId,
            iModelId: iModelConnection.iModelId,
            selectedIds: pendingBackgroundExportJob.selectedIds,
            selectedFeatures: pendingBackgroundExportJob.selectedFeatures,
            filename: pendingBackgroundExportJob.filename,
            trimble: {
              projectId: project.id,
              folderId: folder.id,
              folderPath: folder.path ?? folder.name ?? folder.id,
            },
            featureOptions: pendingBackgroundExportJob.options,
            notificationEmail,
          });

          setActiveBackgroundExportJob(job);
          setPendingBackgroundExportJob(null);
          setPendingIfc(null);
          setUploadSuccess("File export is processing in the background.");
          return;
        } catch (err) {
          console.error("[IFC Export] Background job submission failed:", err);
          setUploadSuccess(null);
          setUploadError((err as Error).message ?? String(err));
          setPendingBackgroundExportJob(null);
          return;
        } finally {
          setIsSubmittingBackgroundJob(false);
        }
      }

      if (!pendingIfc) return;

      setIsUploading(true);

      try {
        const token = pickerAccessToken ?? (await ensureAccessToken());
        if (!token) throw new Error("Trimble access token missing.");
        if (!project?.id) throw new Error("Trimble project missing.");

        await uploadFileToFolder({
          accessToken: token,
          projectId: project.id,
          folderId: folder.id,
          filename: pendingIfc.filename,
          blob: pendingIfc.blob,
        });
        const successMsg = "File export complete.";
        setUploadSuccess(successMsg);

        setPendingIfc(null);
      } catch (err) {
        console.error("[IFC Export] Upload failed:", err);
        setUploadError((err as Error).message ?? String(err));
        setUploadSuccess(null);

        // Safety fallback: if upload fails, user still gets a file
        try {
          downloadBlob(pendingIfc.blob, pendingIfc.filename);
        } catch {
          // ignore
        }
      } finally {
        setIsUploading(false);
      }
    },
    [
      pendingIfc,
      pendingBackgroundExportJob,
      pickerAccessToken,
      ensureAccessToken,
      project?.id,
      iModelConnection?.iTwinId,
      iModelConnection?.iModelId,
    ]
  );

  const activeBuildStatus = isBuildingIfc ? ifcBuildStatus : null;
  const retrieveElapsedMs = activeBuildStatus
    ? (activeBuildStatus.retrieveCompletedAtMs ?? ifcBuildNowMs) - activeBuildStatus.retrieveStartedAtMs
    : 0;
  const writeElapsedMs =
    activeBuildStatus && activeBuildStatus.writeStartedAtMs
      ? (activeBuildStatus.writeCompletedAtMs ?? ifcBuildNowMs) - activeBuildStatus.writeStartedAtMs
      : 0;
  const totalBuildElapsedMs = activeBuildStatus
    ? (activeBuildStatus.writeCompletedAtMs ?? ifcBuildNowMs) - activeBuildStatus.startedAtMs
    : 0;
  const buildPhaseLabel =
    activeBuildStatus?.phase === "retrieving"
      ? "Retrieving Data"
      : activeBuildStatus?.phase === "writing"
        ? "Writing IFC"
        : activeBuildStatus?.phase === "finalizing"
          ? "Finalizing Export"
          : "Building IFC Export";

  return (
    <>
      <style>{`
        @keyframes ifcBuildPulse {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
      <FeatureFilterPanel
        onSelectionChange={(selectedLeafIds) => {
          void iTwinSelectionSync.handleSelectionChange(selectedLeafIds);
        }}
        onActionClick={handleIfcExportClick}
        actionButtonLabel="Import"
        optionLabels={{ primary: "2D", secondary: "3D" }}
        statusContent={
          iTwinSelectionSync.summary ? (
            <div style={{ display: "grid", gap: 4 }}>
              <div style={{ fontWeight: 600 }}>{iTwinSelectionSync.summary}</div>
              {iTwinSelectionSync.lines.map((line, index) => (
                <div key={`${index}-${line}`} style={{ color: "#555" }}>
                  {line}
                </div>
              ))}
            </div>
          ) : null
        }
      />

      {uploadError && (
        <div style={{ padding: 8 }}>
          <b>Upload error:</b> {uploadError}
        </div>
      )}
      {uploadSuccess && (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            maxWidth: 420,
            background: "#1f2937",
            color: "#fff",
            padding: "12px 16px",
            borderRadius: 8,
            boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
            zIndex: 10000,
          }}
          role="status"
          aria-live="polite"
        >
          {uploadSuccess}
        </div>
      )}
      {showExportDebugPanels && activeBackgroundExportJob && (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 72,
            width: 360,
            background:
              activeBackgroundExportJob.status === "failed"
                ? "#3f1d1d"
                : activeBackgroundExportJob.status === "completed"
                  ? "#0f2f1d"
                  : "#0f172a",
            color: "#e5e7eb",
            padding: "12px 14px",
            borderRadius: 8,
            boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
            zIndex: 9999,
            border: "1px solid rgba(148,163,184,0.25)",
          }}
          role="status"
          aria-live="polite"
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Azure Background Import Pilot</div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: "#cbd5e1" }}>
            <div>Job: {activeBackgroundExportJob.jobId.slice(0, 8)}</div>
            <div>Status: {activeBackgroundExportJob.status}</div>
            <div>
              Phase:{" "}
              {activeBackgroundExportJob.progress?.message ??
                activeBackgroundExportJob.progress?.phase ??
                "Waiting for update"}
            </div>
            <div>Progress: {Math.round(activeBackgroundExportJob.progress?.percent ?? 0)}%</div>
            {activeBackgroundExportJob.filename && (
              <div>Target file: {activeBackgroundExportJob.filename}</div>
            )}
            {activeBackgroundExportJob.trimble?.folderPath && (
              <div>Trimble folder: {activeBackgroundExportJob.trimble.folderPath}</div>
            )}
            {activeBackgroundExportJob.notificationEmail && (
              <div>Notify target: {activeBackgroundExportJob.notificationEmail}</div>
            )}
            {activeBackgroundExportJob.notification?.status && (
              <div>
                Notification: {activeBackgroundExportJob.notification.status}
                {activeBackgroundExportJob.notification.message
                  ? ` (${activeBackgroundExportJob.notification.message})`
                  : ""}
              </div>
            )}
            {activeBackgroundExportJob.result?.selectionSummary && (
              <div style={{ marginTop: 6 }}>
                Resolved: {activeBackgroundExportJob.result.selectionSummary.totalMatched3dElements ?? 0} 3D,{" "}
                {activeBackgroundExportJob.result.selectionSummary.totalMatched2dElements ?? 0} direct 2D,{" "}
                {activeBackgroundExportJob.result.selectionSummary.totalRelated2dGraphics ?? 0} related 2D,{" "}
                {activeBackgroundExportJob.result.selectionSummary.totalRelated3dGraphics ?? 0} related 3D
              </div>
            )}
            {activeBackgroundExportJob.result?.geometryProbe && (
              <div style={{ marginTop: 6 }}>
                Geometry probe: {activeBackgroundExportJob.result.geometryProbe.sampledElementCount ?? 0} sampled
                {" "}of {activeBackgroundExportJob.result.geometryProbe.candidateMatched3dElementCount ?? 0} matched
                {" "}3D, {activeBackgroundExportJob.result.geometryProbe.totalMeshCount ?? 0} meshes decoded across{" "}
                {activeBackgroundExportJob.result.geometryProbe.elementsWithMeshes ?? 0} element(s)
                {activeBackgroundExportJob.result.geometryProbe.message
                  ? ` (${activeBackgroundExportJob.result.geometryProbe.message})`
                  : ""}
              </div>
            )}
            {activeBackgroundExportJob.result?.generatedIfc && (
              <div style={{ marginTop: 6 }}>
                IFC artifact: {activeBackgroundExportJob.result.generatedIfc.exportedElementCount ?? 0} exported
                {" "}element(s), {activeBackgroundExportJob.result.generatedIfc.totalTriangleCount ?? 0} triangles
                {activeBackgroundExportJob.result.generatedIfc.message
                  ? ` (${activeBackgroundExportJob.result.generatedIfc.message})`
                  : ""}
              </div>
            )}
            {Array.isArray(activeBackgroundExportJob.result?.artifacts) &&
              activeBackgroundExportJob.result!.artifacts!.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  Artifacts ready:{" "}
                  {activeBackgroundExportJob.result!.artifacts!
                    .map((artifact) => artifact.filename || artifact.kind || artifact.artifactId)
                    .join(", ")}
                </div>
              )}
            {Array.isArray(activeBackgroundExportJob.result?.uploadedFiles) &&
              activeBackgroundExportJob.result.uploadedFiles.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  Trimble uploaded: {activeBackgroundExportJob.result.uploadedFiles.join(", ")}
                </div>
              )}
            {activeBackgroundExportJob.error && (
              <div style={{ color: "#fecaca", marginTop: 6 }}>{activeBackgroundExportJob.error}</div>
            )}
            {activeBackgroundExportJob.status !== "completed" && (
              <div style={{ marginTop: 8, color: "#93c5fd" }}>
                Current MVP note: Azure is doing real iTwin discovery, geometry probing, real IFC generation, Trimble upload, and artifact staging in the background.
              </div>
            )}
          </div>
          <div
            style={{
              position: "relative",
              height: 6,
              background: "#374151",
              borderRadius: 999,
              overflow: "hidden",
              marginTop: 10,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                width: `${Math.max(4, Math.min(100, activeBackgroundExportJob.progress?.percent ?? 0))}%`,
                background:
                  activeBackgroundExportJob.status === "failed"
                    ? "linear-gradient(90deg, #f87171, #ef4444)"
                    : "linear-gradient(90deg, #60a5fa, #22d3ee)",
              }}
            />
          </div>
        </div>
      )}
      {showExportDebugPanels && !isBuildingIfc && lastIfcBuildTiming && (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: activeBackgroundExportJob ? 268 : 72,
            width: 380,
            background: "#0f172a",
            color: "#e5e7eb",
            padding: "12px 14px",
            borderRadius: 8,
            boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
            zIndex: 9999,
            border: "1px solid rgba(148,163,184,0.25)",
          }}
          role="status"
          aria-live="polite"
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Last Export Timing</div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: "#cbd5e1" }}>
            <div>Total (Retrieve + Write): {formatDurationWithMs(lastIfcBuildTiming.totalMs)}</div>
            <div>Retrieving Data: {formatDurationWithMs(lastIfcBuildTiming.retrieveMs)}</div>
            <div>Writing IFC: {formatDurationWithMs(lastIfcBuildTiming.writeMs)}</div>
            <div style={{ marginTop: 6, color: "#93c5fd" }}>
              Retrieval Breakdown (wall clock)
            </div>
            <div>Find IDs: {formatDurationWithMs(lastIfcBuildTiming.findIdsMs)}</div>
            <div>Build Elements: {formatDurationWithMs(lastIfcBuildTiming.buildElementsMs)}</div>
            <div style={{ marginTop: 6, color: "#c4b5fd" }}>
              Build Elements Detail (cumulative, overlaps possible)
            </div>
            <div>Base Props: {formatDurationWithMs(lastIfcBuildTiming.basePropsMs)}</div>
            <div>
              Query/Build Metadata (incl. Base Props):{" "}
              {formatDurationWithMs(lastIfcBuildTiming.basePropsMs + lastIfcBuildTiming.queryMetadataMs)}
            </div>
            <div>Extract Geometry: {formatDurationWithMs(lastIfcBuildTiming.extractGeometryMs)}</div>
            <div>
              Elements: {lastIfcBuildTiming.lightingCount} lighting, {lastIfcBuildTiming.conduitCount} conduit
            </div>
          </div>
        </div>
      )}
      {isBuildingIfc && (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 72,
            width: 320,
            background: "#111827",
            color: "#fff",
            padding: "12px 14px",
            borderRadius: 8,
            boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
            zIndex: 10000,
          }}
          role="status"
          aria-live="polite"
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{buildPhaseLabel}...</div>
          {activeBuildStatus && (
            <div
              style={{
                fontSize: 12,
                lineHeight: 1.4,
                color: "#d1d5db",
                marginBottom: 8,
              }}
            >
              <div>Total: {formatDurationMs(totalBuildElapsedMs)}</div>
              <div>Retrieving Data: {formatDurationMs(retrieveElapsedMs)}</div>
              <div>
                Writing IFC:{" "}
                {activeBuildStatus.writeStartedAtMs
                  ? formatDurationMs(writeElapsedMs)
                  : "0:00.0"}
              </div>
            </div>
          )}
          <div
            style={{
              position: "relative",
              height: 6,
              background: "#374151",
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                width: "50%",
                background: "linear-gradient(90deg, #60a5fa, #22d3ee)",
                animation: "ifcBuildPulse 1.2s ease-in-out infinite",
              }}
            />
          </div>
        </div>
      )}
      {isUploading && (
        <div style={{ padding: 8 }}>
          <b>Uploading IFC to Trimble Connect…</b>
        </div>
      )}

      {showExportDebugPanels && isSubmittingBackgroundJob && (
        <div style={{ padding: 8 }}>
          <b>Submitting background import job to Azure…</b>
        </div>
      )}

      {pickerOpen &&
        (pendingIfc || pendingBackgroundExportJob) &&
        pickerAccessToken &&
        rootFolderId &&
        project?.id && (
        <TrimbleFolderPicker
          open={pickerOpen}
          accessToken={pickerAccessToken}
          projectId={project.id}
          rootFolderId={rootFolderId}
          onCancel={handleFolderPickerCancel}
          onPick={handleFolderPicked}
        />
      )}
    </>
  );
};

export const FeatureExportWidget: React.FC = () => {
  const iModelConnection = useActiveIModelConnection();
  const trimble = useTrimbleConnect();
  const trimbleViewer = trimble.api?.viewer;
  const trimbleSelectionSync = useTrimbleFeatureSelectionSync(iModelConnection, trimbleViewer);
  const [matchSummary, setMatchSummary] = useState<string | null>(null);
  const [matchLines, setMatchLines] = useState<string[]>([]);
  const [flagDesign, setFlagDesign] = useState(false);
  const [notes, setNotes] = useState("");

  const handleExportClick = useCallback(
    async (
      selectedLeafIds: FeatureNodeId[],
      options: {
        includeAsset: boolean;
        includeAsbuilt: boolean;
        include2d: boolean;
        include3d: boolean;
      }
    ) => {
      setMatchSummary("Inspecting current Trimble selection...");
      setMatchLines([]);

      if (!iModelConnection) {
        setMatchSummary("No active iModel connection.");
        return;
      }

      if (!trimbleViewer) {
        setMatchSummary("Trimble viewer API is not available in this session.");
        return;
      }

      try {
        const selection = await trimbleViewer.getSelection();
        const selectedCount = (selection ?? []).reduce(
          (count, entry) => count + (entry.objectRuntimeIds?.length ?? 0),
          0
        );

        console.groupCollapsed("[iTwin Export] Trimble selection");
        console.log("Tree selection:", selectedLeafIds);
        console.log("Export options:", options);
        console.log("Trimble raw selection payload:", selection);
        console.log("Trimble selected object count:", selectedCount);
        console.groupEnd();

        if (!selectedCount) {
          setMatchSummary("No Trimble objects are currently selected.");
          return;
        }

        const selectedObjects = await trimbleViewer.getObjects({
          modelObjectIds: selection,
        });
        const detailedSelectedObjects = await Promise.all(
          (selection ?? []).map(async (entry) => {
            const runtimeIds = entry.objectRuntimeIds ?? [];
            if (!runtimeIds.length) {
              return {
                modelId: entry.modelId,
                objects: [] as Awaited<
                  ReturnType<typeof trimbleViewer.getObjectProperties>
                >,
              };
            }

            const objects = await trimbleViewer.getObjectProperties(
              entry.modelId,
              runtimeIds
            );
            return {
              modelId: entry.modelId,
              objects,
            };
          })
        );

        const trimbleObjects = detailedSelectedObjects.flatMap((entry) =>
          (entry.objects ?? []).map((object) => ({
            modelId: entry.modelId,
            object,
          }))
        );

        console.groupCollapsed("[iTwin Export] Trimble object details");
        console.log("Trimble getObjects payload:", selectedObjects);
        console.log("Trimble getObjectProperties payload:", detailedSelectedObjects);
        console.table(
          trimbleObjects.map(({ modelId, object }) => ({
            modelId,
            objectId: object.id,
            productName: String(object.product?.name ?? ""),
            productType: String(object.product?.objectType ?? ""),
            federationGuid: getTrimblePropertyValue(object, "Federation GUID"),
            userLabel: getTrimblePropertyValue(object, "User Label"),
            propertySets: (object.properties ?? [])
              .map((set) => set.set ?? "(unnamed)")
              .join(" | "),
            propertyNames: (object.properties ?? [])
              .flatMap((set) => (set.properties ?? []).map((prop) => prop.name ?? ""))
              .join(" | "),
          }))
        );
        for (const { modelId, object } of trimbleObjects) {
          console.log(
            `Trimble model ${modelId} object ${object.id} raw properties:`,
            object.properties ?? []
          );
        }
        console.groupEnd();

        if (!trimbleObjects.length) {
          setMatchSummary("Trimble returned no detailed object properties for the current selection.");
          return;
        }

        const candidates = uniqueNonEmptyStrings(
          trimbleObjects.map(({ object }) => getTrimbleSourceElementId(object))
        ).map((elementId) => ({
          elementId,
          federationGuid: "",
          ifcGuid: "",
          userLabel: "",
        }));

        if (!candidates.length) {
          setMatchSummary(
            `Trimble returned ${trimbleObjects.length} selected object(s), but none had a Source Element Id to map back to the iTwin.`
          );
          setMatchLines(
            trimbleObjects.map((entry) => `${describeTrimbleObjectSelection(entry)} -> no Source Element Id was found in Trimble.`)
          );
          return;
        }

        console.groupCollapsed("[iTwin Export] Trimble selection source iTwin targets");
        console.table(
          candidates.map((candidate) => ({
            elementId: candidate.elementId,
          }))
        );
        console.groupEnd();

        const bySourceElementId = new Map<
          string,
          { elementId: string; federationGuid: string; ifcGuid: string; userLabel: string }
        >();
        const byIfcGuid = new Map<
          string,
          { elementId: string; federationGuid: string; ifcGuid: string; userLabel: string }
        >();
        const byUserLabel = new Map<
          string,
          { elementId: string; federationGuid: string; ifcGuid: string; userLabel: string }[]
        >();
        const byNormalizedUserLabel = new Map<
          string,
          { elementId: string; federationGuid: string; ifcGuid: string; userLabel: string }[]
        >();

        for (const candidate of candidates) {
          bySourceElementId.set(candidate.elementId.trim().toLowerCase(), candidate);
          if (candidate.ifcGuid) {
            byIfcGuid.set(candidate.ifcGuid.trim().toLowerCase(), candidate);
          }
          const labelKey = candidate.userLabel.trim().toLowerCase();
          if (labelKey) {
            const existing = byUserLabel.get(labelKey);
            if (existing) {
              existing.push(candidate);
            } else {
              byUserLabel.set(labelKey, [candidate]);
            }
          }
          const normalizedLabelKey = normalizeMatchToken(candidate.userLabel);
          if (normalizedLabelKey) {
            const existingNormalized = byNormalizedUserLabel.get(normalizedLabelKey);
            if (existingNormalized) {
              existingNormalized.push(candidate);
            } else {
              byNormalizedUserLabel.set(normalizedLabelKey, [candidate]);
            }
          }
        }

        const lines: string[] = [];
        let matchedCount = 0;
        const matchedElements: Array<{ elementId: string; description: string }> = [];
        const seenMatchedElementIds = new Set<string>();

        for (const { modelId, object } of trimbleObjects) {
          const trimbleSourceElementId = getTrimbleSourceElementId(object);
          const trimbleIfcGuid = getTrimblePropertyValue(object, "Federation GUID")
            .trim()
            .toLowerCase();
          const trimbleUserLabel =
            getTrimblePropertyValue(object, "User Label").trim() ||
            String(object.product?.name ?? "").trim();
          const trimbleHints = collectTrimbleMatchHints(object);

          let match = trimbleSourceElementId
            ? bySourceElementId.get(trimbleSourceElementId.trim().toLowerCase())
            : undefined;
          let matchBasis = "source element id";

          if (!match) {
            match = trimbleIfcGuid ? byIfcGuid.get(trimbleIfcGuid) : undefined;
            matchBasis = "IFC guid";
          }

          if (!match) {
            for (const guidHint of trimbleHints.guidHints) {
              const hinted = byIfcGuid.get(guidHint);
              if (hinted) {
                match = hinted;
                matchBasis = "guid hint";
                break;
              }
            }
          }

          if (!match && trimbleUserLabel) {
            const labelMatches = byUserLabel.get(trimbleUserLabel.toLowerCase()) ?? [];
            if (labelMatches.length === 1) {
              match = labelMatches[0];
              matchBasis = "user label";
            }
          }

          if (!match) {
            for (const labelHint of trimbleHints.labelHints) {
              const exactLabelMatches = byUserLabel.get(labelHint.toLowerCase()) ?? [];
              if (exactLabelMatches.length === 1) {
                match = exactLabelMatches[0];
                matchBasis = "label hint";
                break;
              }

              const normalizedLabelHint = normalizeMatchToken(labelHint);
              if (!normalizedLabelHint) continue;
              const normalizedMatches = byNormalizedUserLabel.get(normalizedLabelHint) ?? [];
              if (normalizedMatches.length === 1) {
                match = normalizedMatches[0];
                matchBasis = "normalized label hint";
                break;
              }
            }
          }

          console.log("[iTwin Export] Trimble selection match comparison", {
            trimbleModelId: modelId,
            trimbleObjectId: object.id,
            trimbleSourceElementId,
            trimbleIfcGuid,
            trimbleUserLabel,
            trimbleGuidHints: trimbleHints.guidHints,
            trimbleLabelHints: trimbleHints.labelHints,
            trimbleProductName: String(object.product?.name ?? ""),
            trimblePropertySets: object.properties ?? [],
            matchedElementId: match?.elementId ?? null,
            matchedFederationGuid: match?.federationGuid ?? null,
            matchedIfcGuid: match?.ifcGuid ?? null,
            matchedUserLabel: match?.userLabel ?? null,
            matchBasis: match ? matchBasis : null,
          });

          if (match) {
            matchedCount += 1;
            const description = `${trimbleUserLabel || trimbleIfcGuid || `Trimble object ${object.id}`} -> ${match.elementId} (${matchBasis})`;
            if (!seenMatchedElementIds.has(match.elementId)) {
              matchedElements.push({
                elementId: match.elementId,
                description,
              });
              seenMatchedElementIds.add(match.elementId);
            }
            lines.push(description);
          } else {
            lines.push(
              `${trimbleUserLabel || trimbleIfcGuid || `Trimble object ${object.id}`} -> no mapped iTwin match`
            );
          }
        }

        let writebackMessage: string | null = null;
        if (matchedElements.length > 0) {
          const backendBase = getBackendBase();
          if (!backendBase) {
            writebackMessage =
              "Matches were found, but no backend base is configured for the live export request.";
          } else {
            const token = String((await IModelApp.getAccessToken()) ?? "").trim();
            if (!token) {
              writebackMessage =
                "Matches were found, but the Bentley access token was unavailable for the backend export request.";
            } else {
              const authorization = token.startsWith("Bearer ")
                ? token
                : `Bearer ${token}`;
              const backendRoot = backendBase.replace(/\/$/, "");
              const targetElements = matchedElements.map((entry) => entry.elementId);
              const response = await fetch(`${backendRoot}/itwin/update-aspect-properties-batch`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: authorization,
                },
                body: JSON.stringify({
                  iTwinId: iModelConnection?.iTwinId ?? "",
                  iModelId: iModelConnection?.iModelId ?? "",
                  aspectClassFullName: NICECONNECT_ASPECT_CLASS,
                  changeDescription: "NiceConnect Export writeback from Trimble selection",
                  dryRun: false,
                  targets: targetElements.map((elementId) => ({
                    elementId,
                    properties: [
                      {
                        requestedPropertyNames: ["Design_Issue", "Design Issue", "Flag Design"],
                        label: "Flag Design",
                        newValue: flagDesign,
                      },
                      {
                        requestedPropertyNames: ["Notes"],
                        label: "Notes",
                        newValue: notes,
                      },
                    ],
                  })),
                }),
              });

              const responseText = await response.text();
              let responseJson: any = null;
              try {
                responseJson = responseText ? JSON.parse(responseText) : null;
              } catch {
                responseJson = null;
              }

              const updatedElementIds = Array.isArray(responseJson?.updatedElementIds)
                ? responseJson.updatedElementIds.map((value: unknown) => String(value ?? "").trim()).filter(Boolean)
                : [];
              const failedMessages = Array.isArray(responseJson?.failedMessages)
                ? responseJson.failedMessages.map((value: unknown) => String(value ?? "").trim()).filter(Boolean)
                : [];
              const skippedMessages = Array.isArray(responseJson?.skippedMessages)
                ? responseJson.skippedMessages.map((value: unknown) => String(value ?? "").trim()).filter(Boolean)
                : [];

              if (!response.ok) {
                const errorMessage =
                  responseJson?.error ??
                  responseText ??
                  `Batch writeback failed (${response.status}).`;
                writebackMessage =
                  failedMessages.length > 0
                    ? `${errorMessage} Issues: ${failedMessages.join(" ")}`
                    : errorMessage;
                if (skippedMessages.length > 0) {
                  lines.push(...skippedMessages);
                }
                setMatchSummary(
                  `Matched ${matchedCount} of ${trimbleObjects.length} selected Trimble object(s) to ${candidates.length} source iTwin element(s). ${writebackMessage}`
                );
                setMatchLines(lines);
                return;
              }

              if (updatedElementIds.length > 0) {
                writebackMessage = `Export updated ${updatedElementIds.length} matched iTwin element(s): ${updatedElementIds.join(", ")}.`;
              }
              if (failedMessages.length > 0) {
                writebackMessage = writebackMessage
                  ? `${writebackMessage} Issues: ${failedMessages.join(" ")}`
                  : `Export found matches, but writeback had issues: ${failedMessages.join(" ")}`;
              } else if (!updatedElementIds.length && skippedMessages.length > 0) {
                writebackMessage =
                  "Export found mapped iTwin elements, but the NiceConnect aspect did not expose matching Flag Design or Notes properties.";
              }
              if (skippedMessages.length > 0) {
                lines.push(...skippedMessages);
              }
            }
          }
        }

        setMatchSummary(
          `Matched ${matchedCount} of ${trimbleObjects.length} selected Trimble object(s) to ${candidates.length} source iTwin element(s).${writebackMessage ? ` ${writebackMessage}` : ""}`
        );
        setMatchLines(lines);

        console.info("[iTwin Export] Trimble selection writeback", {
          selectedLeafIds,
          options,
          selectedCount,
          trimbleObjects: trimbleObjects.length,
          candidateCount: candidates.length,
          matchedCount,
          matchedElementIds: matchedElements.map((entry) => entry.elementId),
          flagDesign,
          notes,
          writebackMessage,
          lines,
        });
      } catch (err) {
        console.error("[iTwin Export] Trimble selection writeback failed:", err);
        setMatchSummary((err as Error).message ?? "Trimble selection export failed.");
      }
    },
    [flagDesign, iModelConnection, notes, trimbleViewer]
  );

  return (
    <FeatureFilterPanel
      onSelectionChange={(selectedLeafIds) => {
        void trimbleSelectionSync.handleSelectionChange(selectedLeafIds);
      }}
      onActionClick={handleExportClick}
      actionButtonLabel="Export"
      hideDefaultOptions
      extraControls={
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ color: "#4b5563", fontSize: 12, lineHeight: 1.4 }}>
            Export uses the current Trimble Connect selection. The feature filters can still help select
            Trimble objects, but they are not required before submitting.
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={flagDesign}
              onChange={(event) => setFlagDesign(Boolean(event.target.checked))}
            />
            <span>Flag Design</span>
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Notes</span>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={4}
              placeholder="Send notes to the matched iTwin element(s)"
              style={{
                width: "100%",
                resize: "vertical",
                fontFamily: "inherit",
                fontSize: 12,
                padding: 8,
                border: "1px solid #c8ccd0",
                borderRadius: 4,
              }}
            />
          </label>
        </div>
      }
      statusContent={
        trimbleSelectionSync.summary || matchSummary ? (
          <div style={{ display: "grid", gap: 4 }}>
            {trimbleSelectionSync.summary ? (
              <>
                <div style={{ fontWeight: 600 }}>{trimbleSelectionSync.summary}</div>
                {trimbleSelectionSync.lines.map((line, index) => (
                  <div key={`trimble-sync-${index}-${line}`} style={{ color: "#555" }}>
                    {line}
                  </div>
                ))}
              </>
            ) : null}
            {matchSummary ? (
              <>
                <div style={{ fontWeight: 600 }}>{matchSummary}</div>
                {matchLines.map((line, index) => (
                  <div key={`${index}-${line}`} style={{ color: "#555" }}>
                    {line}
                  </div>
                ))}
              </>
            ) : null}
          </div>
        ) : null
      }
    />
  );
};

type TrimbleSelectionObject = {
  modelId: string;
  object: {
    id: number;
    product?: { name?: string; objectType?: string };
    properties?: Array<{ set?: string; properties?: Array<{ name?: string; value?: unknown }> }>;
  };
};

type TrimbleSelectionDetails = {
  objects: TrimbleSelectionObject[];
  selectedCount: number;
  selection: Array<{ modelId: string; objectRuntimeIds?: number[] }>;
};

type FieldInputDebugPropertySet = {
  properties: Array<{ name: string; value: unknown }>;
  set: string;
};

type FieldInputDebugObject = {
  description: string;
  entityIdHints: number[];
  existingFieldInput: TrimbleFieldInputSnapshot;
  guidHints: string[];
  labelHints: string[];
  productName: string;
  productType: string;
  propertySets: FieldInputDebugPropertySet[];
  sourceElementId: string;
  trimbleObjectId: number;
  userLabel: string;
};

type FieldInputDebugModel = {
  entityIdHints: number[];
  error?: string;
  externalObjectIds: string[];
  filename?: string;
  folderId?: string;
  guidHints: string[];
  ifcTextDebug?: {
    containsEntityIdHints: number[];
    containsNiceConnectAzureExport: boolean;
    containsNiceConnectExport: boolean;
    containsSourceElementIdHints: string[];
    containsSourceElementIdProperty: boolean;
    matchingLines: string[];
    textLength: number;
  };
  missingEntityIds: number[];
  matchedIfcEntityCount?: number;
  missingObjectIds: string[];
  missingSourceElementIds: string[];
  modelId: string;
  normalizedObjectIds: string[];
  rawObjectIds: string[];
  runtimeIds: number[];
  selectedObjects: FieldInputDebugObject[];
  sourceElementIdHints: string[];
};

type FieldInputDebugSnapshot = {
  models: FieldInputDebugModel[];
  selectedCount: number;
  selection: Array<{ modelId: string; objectRuntimeIds: number[] }>;
  submittedAt: string;
  values: FieldInputValues;
};

type TrimbleFieldInputSnapshot = {
  flagDesign?: boolean;
  notes?: string;
  asBuiltX?: number;
  asBuiltY?: number;
  asBuiltZ?: number;
};

type AggregatedTrimbleWriteback = {
  conflicts: string[];
  elementId: string;
  sourceDescriptions: string[];
  values: TrimbleFieldInputSnapshot;
};

type AspectWriteInstruction = {
  changeDescription: string;
  newValue: boolean | number | string;
  propertyName: string;
};

function getTrimblePropertyRawValueByCandidates(
  object: {
    properties?: Array<{ properties?: Array<{ name?: string; value?: unknown }> }>;
  },
  propertyNames: string[]
): unknown {
  for (const propertyName of propertyNames) {
    const value = getTrimblePropertyRawValue(object, propertyName);
    if (value !== undefined) {
      return value;
    }
  }

  return undefined;
}

function getTrimblePropertyValueByCandidates(
  object: {
    properties?: Array<{ properties?: Array<{ name?: string; value?: unknown }> }>;
  },
  propertyNames: string[]
): string {
  const rawValue = getTrimblePropertyRawValueByCandidates(object, propertyNames);
  if (rawValue === null || rawValue === undefined) return "";
  return String(rawValue).trim();
}

function parseTrimbleBooleanValue(rawValue: unknown): boolean | undefined {
  if (rawValue === null || rawValue === undefined) return undefined;
  if (typeof rawValue === "boolean") return rawValue;
  const normalized = String(rawValue).trim().toLowerCase();
  if (!normalized) return undefined;
  if (["1", "true", "yes", "y", "on", ".t."].includes(normalized)) return true;
  if (["0", "false", "no", "n", "off", ".f."].includes(normalized)) return false;
  return undefined;
}

function parseTrimbleNumberValue(rawValue: unknown): number | undefined {
  if (rawValue === null || rawValue === undefined) return undefined;
  const normalized = String(rawValue).trim();
  if (!normalized) return undefined;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function describeTrimbleObjectSelection(selection: TrimbleSelectionObject): string {
  const userLabel = getTrimblePropertyValue(selection.object, "User Label");
  const productName = String(selection.object.product?.name ?? "").trim();
  return (
    userLabel ||
    productName ||
    `Trimble object ${selection.object.id} (${selection.modelId})`
  );
}

function hasAnyTrimbleFieldInputValue(values: TrimbleFieldInputSnapshot): boolean {
  return (
    values.flagDesign !== undefined ||
    values.notes !== undefined ||
    values.asBuiltX !== undefined ||
    values.asBuiltY !== undefined ||
    values.asBuiltZ !== undefined
  );
}

function readTrimbleFieldInputFromObject(
  object: TrimbleSelectionObject["object"]
): TrimbleFieldInputSnapshot {
  const rawNotes = getTrimblePropertyRawValueByCandidates(object, [
    FIELD_INPUT_NOTES_PROPERTY,
  ]);

  return {
    flagDesign: parseTrimbleBooleanValue(
      getTrimblePropertyRawValueByCandidates(object, [
        FIELD_INPUT_FLAG_DESIGN_PROPERTY,
        "Design Issue",
        "Design_Issue",
      ])
    ),
    notes: rawNotes === undefined ? undefined : String(rawNotes ?? "").trim(),
    asBuiltX: parseTrimbleNumberValue(
      getTrimblePropertyRawValueByCandidates(object, [
        FIELD_INPUT_AS_BUILT_X_PROPERTY,
        "AsBuilt X",
        "AsBuilt_X",
        "AsBuiltX",
        "X",
      ])
    ),
    asBuiltY: parseTrimbleNumberValue(
      getTrimblePropertyRawValueByCandidates(object, [
        FIELD_INPUT_AS_BUILT_Y_PROPERTY,
        "AsBuilt Y",
        "AsBuilt_Y",
        "AsBuiltY",
        "Y",
      ])
    ),
    asBuiltZ: parseTrimbleNumberValue(
      getTrimblePropertyRawValueByCandidates(object, [
        FIELD_INPUT_AS_BUILT_Z_PROPERTY,
        "AsBuilt Z",
        "AsBuilt_Z",
        "AsBuiltZ",
        "Z",
      ])
    ),
  };
}

function getTrimbleSourceElementId(object: TrimbleSelectionObject["object"]): string {
  return getTrimblePropertyValueByCandidates(object, [
    "Source Element Id",
    "NiceConnect Azure Export.Source Element Id",
    "NiceConnect Export.Source Element Id",
    "SourceElementId",
  ]);
}

function mergeTrimbleFieldInputSnapshots(args: {
  current: TrimbleFieldInputSnapshot;
  incoming: TrimbleFieldInputSnapshot;
  objectDescription: string;
}): { conflicts: string[]; next: TrimbleFieldInputSnapshot } {
  const next: TrimbleFieldInputSnapshot = { ...args.current };
  const conflicts: string[] = [];

  const mergeValue = (key: keyof TrimbleFieldInputSnapshot, label: string) => {
    const currentValue = next[key];
    const incomingValue = args.incoming[key];
    if (incomingValue === undefined) return;
    if (currentValue === undefined) {
      (next as Record<string, unknown>)[key] = incomingValue;
      return;
    }
    if (Object.is(currentValue, incomingValue)) return;
    conflicts.push(
      `${args.objectDescription} had a different ${label} value; keeping the first value found for ${label}.`
    );
  };

  mergeValue("flagDesign", "Flag Design");
  mergeValue("notes", "Notes");
  mergeValue("asBuiltX", "As Built X");
  mergeValue("asBuiltY", "As Built Y");
  mergeValue("asBuiltZ", "As Built Z");

  return { conflicts, next };
}

async function getTrimbleSelectionDetails(trimbleViewer: any): Promise<TrimbleSelectionDetails> {
  const selection = ((await trimbleViewer.getSelection()) ?? []) as Array<{
    modelId: string;
    objectRuntimeIds?: number[];
  }>;
  const selectedCount = selection.reduce(
    (count, entry) => count + (entry.objectRuntimeIds?.length ?? 0),
    0
  );

  if (!selectedCount) {
    return { selection, selectedCount: 0, objects: [] };
  }

  const detailedSelectedObjects = await Promise.all(
    selection.map(async (entry) => {
      const runtimeIds = entry.objectRuntimeIds ?? [];
      if (!runtimeIds.length) {
        return { modelId: entry.modelId, objects: [] as TrimbleSelectionObject["object"][] };
      }

      const objects = await trimbleViewer.getObjectProperties(entry.modelId, runtimeIds);
      return {
        modelId: entry.modelId,
        objects: (objects ?? []) as TrimbleSelectionObject["object"][],
      };
    })
  );

  return {
    selection,
    selectedCount,
    objects: detailedSelectedObjects.flatMap((entry) =>
      entry.objects.map((object) => ({
        modelId: entry.modelId,
        object,
      }))
    ),
  };
}

function sanitizeTrimbleDebugValue(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value ?? null;
  if (typeof value === "boolean" || typeof value === "number") return value;
  if (typeof value === "string") {
    return value.length > 500 ? `${value.slice(0, 500)}... [trimmed ${value.length - 500} chars]` : value;
  }

  if (depth >= 2) {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((entry) => sanitizeTrimbleDebugValue(entry, depth + 1));
  }

  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>).slice(0, 20)) {
      out[key] = sanitizeTrimbleDebugValue(entry, depth + 1);
    }
    return out;
  }

  return String(value);
}

function buildTrimblePropertySetsForDebug(
  object: TrimbleSelectionObject["object"]
): FieldInputDebugPropertySet[] {
  return (object.properties ?? [])
    .map((set) => ({
      set: String(set.set ?? "").trim(),
      properties: (set.properties ?? []).map((prop) => ({
        name: String(prop.name ?? "").trim(),
        value: sanitizeTrimbleDebugValue(prop.value),
      })),
    }))
    .filter((set) => set.set.length > 0 || set.properties.length > 0);
}

function buildFieldInputDebugObject(selection: TrimbleSelectionObject): FieldInputDebugObject {
  const matchHints = collectTrimbleMatchHints(selection.object);
  return {
    description: describeTrimbleObjectSelection(selection),
    entityIdHints: matchHints.entityIdHints,
    existingFieldInput: readTrimbleFieldInputFromObject(selection.object),
    guidHints: matchHints.guidHints,
    labelHints: matchHints.labelHints,
    productName: String(selection.object.product?.name ?? "").trim(),
    productType: String(selection.object.product?.objectType ?? "").trim(),
    propertySets: buildTrimblePropertySetsForDebug(selection.object),
    sourceElementId: getTrimbleSourceElementId(selection.object),
    trimbleObjectId: selection.object.id,
    userLabel: getTrimblePropertyValue(selection.object, "User Label"),
  };
}

function buildIfcTextDebugSnapshot(args: {
  entityIdHints: number[];
  ifcText: string;
  sourceElementIdHints: string[];
}): NonNullable<FieldInputDebugModel["ifcTextDebug"]> {
  const normalizedIfcText = args.ifcText.toLowerCase();
  const interestingLinePatterns = [
    ...args.sourceElementIdHints.map((value) => value.toLowerCase()),
    ...args.entityIdHints.map((value) => `#${value}=`.toLowerCase()),
    "source element id",
    "niceconnect export",
    "niceconnect azure export",
  ];
  const matchingLines = args.ifcText
    .split(/\r?\n/)
    .filter((line) => {
      const normalizedLine = line.toLowerCase();
      return interestingLinePatterns.some((pattern) => normalizedLine.includes(pattern));
    })
    .slice(0, 20)
    .map((line) => (line.length > 500 ? `${line.slice(0, 500)}... [trimmed]` : line));

  return {
    containsEntityIdHints: args.entityIdHints.filter((value) =>
      new RegExp(`#${value}\\s*=`, "i").test(args.ifcText)
    ),
    containsNiceConnectAzureExport: normalizedIfcText.includes("niceconnect azure export"),
    containsNiceConnectExport: normalizedIfcText.includes("niceconnect export"),
    containsSourceElementIdHints: args.sourceElementIdHints.filter((value) =>
      normalizedIfcText.includes(value.toLowerCase())
    ),
    containsSourceElementIdProperty: normalizedIfcText.includes("source element id"),
    matchingLines,
    textLength: args.ifcText.length,
  };
}

function readStoredFieldInputDraft(): FieldInputFormState {
  if (typeof window === "undefined") {
    return { flagDesign: false, notes: "", asBuiltX: "", asBuiltY: "", asBuiltZ: "" };
  }

  try {
    const raw = window.sessionStorage.getItem(FIELD_INPUT_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<FieldInputFormState>) : {};
    return {
      flagDesign: Boolean(parsed.flagDesign),
      notes: String(parsed.notes ?? ""),
      asBuiltX: String(parsed.asBuiltX ?? ""),
      asBuiltY: String(parsed.asBuiltY ?? ""),
      asBuiltZ: String(parsed.asBuiltZ ?? ""),
    };
  } catch {
    return { flagDesign: false, notes: "", asBuiltX: "", asBuiltY: "", asBuiltZ: "" };
  }
}

function persistFieldInputDraft(formState: FieldInputFormState): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(FIELD_INPUT_STORAGE_KEY, JSON.stringify(formState));
  } catch {
    // ignore storage errors
  }
}

async function readTrimbleLoadedModelText(blobOrString: Blob | string | undefined): Promise<string> {
  if (blobOrString instanceof Blob) {
    return blobOrString.text();
  }

  if (typeof blobOrString === "string") {
    const base64Payload = blobOrString.includes(",")
      ? blobOrString.slice(blobOrString.indexOf(",") + 1)
      : blobOrString;
    const binary = window.atob(base64Payload);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
  }

  throw new Error("Trimble did not provide the loaded IFC file content for the selected model.");
}

function buildAspectWriteInstructions(
  aspectRow: NiceConnectAspectRow,
  values: TrimbleFieldInputSnapshot
): { instructions: AspectWriteInstruction[]; skipped: string[] } {
  const instructions: AspectWriteInstruction[] = [];
  const skipped: string[] = [];

  const maybeAddInstruction = (
    requestedNames: string[],
    value: boolean | number | string | undefined,
    label: string,
    changeDescription: string
  ) => {
    if (value === undefined) return;

    const resolvedPropertyName = resolveAspectPropertyName(aspectRow, requestedNames);
    if (!resolvedPropertyName) {
      skipped.push(`${label} is not present on the NiceConnect aspect.`);
      return;
    }

    instructions.push({
      propertyName: resolvedPropertyName,
      newValue: value,
      changeDescription,
    });
  };

  maybeAddInstruction(
    ["Design_Issue", "Design Issue", "Flag Design"],
    values.flagDesign,
    "Flag Design",
    "NiceConnect Design Issue writeback from Trimble export"
  );
  maybeAddInstruction(
    ["Notes"],
    values.notes,
    "Notes",
    "NiceConnect Notes writeback from Trimble export"
  );
  maybeAddInstruction(
    ["AsBuilt_X", "As Built X", "AsBuiltX", "X"],
    values.asBuiltX,
    "As Built X",
    "NiceConnect As Built X writeback from Trimble export"
  );
  maybeAddInstruction(
    ["AsBuilt_Y", "As Built Y", "AsBuiltY", "Y"],
    values.asBuiltY,
    "As Built Y",
    "NiceConnect As Built Y writeback from Trimble export"
  );
  maybeAddInstruction(
    ["AsBuilt_Z", "As Built Z", "AsBuiltZ", "Z"],
    values.asBuiltZ,
    "As Built Z",
    "NiceConnect As Built Z writeback from Trimble export"
  );

  return { instructions, skipped };
}

export const FeatureExportWritebackWidget: React.FC = () => {
  const iModelConnection = useActiveIModelConnection();
  const trimble = useTrimbleConnect();
  const trimbleViewer = trimble.api?.viewer;
  const trimbleSelectionSync = useTrimbleFeatureSelectionSync(iModelConnection, trimbleViewer);
  const [matchSummary, setMatchSummary] = useState<string | null>(null);
  const [matchLines, setMatchLines] = useState<string[]>([]);

  const handleExportClick = useCallback(
    async (
      selectedLeafIds: FeatureNodeId[],
      options: {
        includeAsset: boolean;
        includeAsbuilt: boolean;
        include2d: boolean;
        include3d: boolean;
      }
    ) => {
      setMatchSummary("Inspecting current Trimble selection...");
      setMatchLines([]);

      if (!iModelConnection) {
        setMatchSummary("No active iModel connection.");
        return;
      }

      if (!trimbleViewer) {
        setMatchSummary("Trimble viewer API is not available in this session.");
        return;
      }

      try {
        const selectionDetails = await getTrimbleSelectionDetails(trimbleViewer);
        if (!selectionDetails.selectedCount) {
          setMatchSummary("No Trimble objects are currently selected.");
          return;
        }

        const backendBase = getBackendBase();
        if (!backendBase) {
          setMatchSummary("NiceConnect backend base is not configured.");
          return;
        }

        const token = String((await IModelApp.getAccessToken()) ?? "").trim();
        if (!token) {
          setMatchSummary("Bentley access token is unavailable.");
          return;
        }

        const authorization = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
        const backendRoot = backendBase.replace(/\/$/, "");
        const lines: string[] = [];
        const aggregatedByElementId = new Map<string, AggregatedTrimbleWriteback>();

        for (const selectionObject of selectionDetails.objects) {
          const description = describeTrimbleObjectSelection(selectionObject);
          const sourceElementId = getTrimbleSourceElementId(selectionObject.object);
          if (!sourceElementId) {
            lines.push(`${description} -> no Source Element Id was found in Trimble.`);
            continue;
          }

          const fieldInputValues = readTrimbleFieldInputFromObject(selectionObject.object);
          if (!hasAnyTrimbleFieldInputValue(fieldInputValues)) {
            lines.push(`${description} -> ${sourceElementId} (no Field Input values found in Trimble).`);
            continue;
          }

          const existing = aggregatedByElementId.get(sourceElementId);
          if (!existing) {
            aggregatedByElementId.set(sourceElementId, {
              elementId: sourceElementId,
              sourceDescriptions: [description],
              conflicts: [],
              values: fieldInputValues,
            });
          } else {
            const merged = mergeTrimbleFieldInputSnapshots({
              current: existing.values,
              incoming: fieldInputValues,
              objectDescription: description,
            });
            existing.values = merged.next;
            existing.conflicts.push(...merged.conflicts);
            existing.sourceDescriptions.push(description);
          }

          lines.push(`${description} -> ${sourceElementId}`);
        }

        if (!aggregatedByElementId.size) {
          setMatchSummary(
            `Trimble returned ${selectionDetails.objects.length} selected object(s), but none had both a Source Element Id and Field Input values to export.`
          );
          setMatchLines(lines);
          return;
        }

        const queryAspectRowForElement = async (elementId: string): Promise<NiceConnectAspectRow | null> => {
          const rows = await queryNiceConnectAspectsForElement({
            accessToken: authorization,
            backendBase: backendRoot,
            elementId,
            iTwinId: String(iModelConnection.iTwinId ?? "").trim(),
            iModelId: String(iModelConnection.iModelId ?? "").trim(),
          });
          return rows[0] ?? null;
        };

        const checkLockStatus = async (elementId: string): Promise<string | null> => {
          const response = await fetch(`${backendRoot}/itwin/writeback-lock-status`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: authorization,
            },
            body: JSON.stringify({
              iTwinId: iModelConnection.iTwinId ?? "",
              iModelId: iModelConnection.iModelId ?? "",
              elementId,
            }),
          });

          const responseText = await response.text();
          let responseJson: any = null;
          try {
            responseJson = responseText ? JSON.parse(responseText) : null;
          } catch {
            responseJson = null;
          }

          if (
            response.status === 409 &&
            (responseJson?.lockConflict || responseJson?.locked)
          ) {
            const precheckDetails = [
              responseJson?.conflictSummary ?? "",
              responseJson?.recoveryHint ?? "",
            ]
              .filter((value) => String(value).trim().length > 0)
              .join(" ");
            return `${elementId} is currently locked.${precheckDetails ? ` ${precheckDetails}` : ""}`;
          }

          return null;
        };

        const updateElementProperty = async (
          elementId: string,
          instruction: AspectWriteInstruction
        ) => {
          const response = await fetch(`${backendRoot}/itwin/update-aspect-property`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: authorization,
            },
            body: JSON.stringify({
              iTwinId: iModelConnection.iTwinId ?? "",
              iModelId: iModelConnection.iModelId ?? "",
              elementId,
              aspectClassFullName: NICECONNECT_ASPECT_CLASS,
              propertyName: instruction.propertyName,
              newValue: instruction.newValue,
              dryRun: false,
              changeDescription: instruction.changeDescription,
            }),
          });

          const responseText = await response.text();
          let responseJson: any = null;
          try {
            responseJson = responseText ? JSON.parse(responseText) : null;
          } catch {
            responseJson = null;
          }

          return {
            ok: response.ok,
            status: response.status,
            responseText,
            responseJson,
          };
        };

        const updatedElementIds: string[] = [];
        const failedMessages: string[] = [];
        const skippedMessages: string[] = [];

        for (const aggregate of aggregatedByElementId.values()) {
          const aspectRow = await queryAspectRowForElement(aggregate.elementId);
          if (!aspectRow) {
            failedMessages.push(
              `${aggregate.elementId} does not have a NiceConnect aspect to receive Trimble values.`
            );
            continue;
          }

          const { instructions, skipped } = buildAspectWriteInstructions(
            aspectRow,
            aggregate.values
          );
          if (aggregate.conflicts.length > 0) {
            skippedMessages.push(...aggregate.conflicts);
          }
          if (skipped.length > 0) {
            skippedMessages.push(`${aggregate.elementId}: ${skipped.join(" ")}`);
          }
          if (!instructions.length) {
            skippedMessages.push(
              `${aggregate.elementId} did not have any matching NiceConnect aspect properties for the selected Trimble values.`
            );
            continue;
          }

          const lockMessage = await checkLockStatus(aggregate.elementId);
          if (lockMessage) {
            failedMessages.push(lockMessage);
            continue;
          }

          let elementFailed = false;
          for (const instruction of instructions) {
            const result = await updateElementProperty(aggregate.elementId, instruction);
            if (!result.ok) {
              const availableProperties = Array.isArray(result.responseJson?.availableProperties)
                ? result.responseJson.availableProperties
                : [];
              const errorMessage =
                result.responseJson?.error ??
                result.responseText ??
                `Writeback failed (${result.status}).`;
              failedMessages.push(
                availableProperties.length > 0
                  ? `${aggregate.elementId} ${instruction.propertyName} failed: ${errorMessage} Available properties: ${availableProperties.join(
                      ", "
                    )}`
                  : `${aggregate.elementId} ${instruction.propertyName} failed: ${errorMessage}`
              );
              elementFailed = true;
              break;
            }
          }

          if (!elementFailed) {
            updatedElementIds.push(aggregate.elementId);
          }
        }

        const summaryParts: string[] = [
          `Trimble selection contained ${selectionDetails.objects.length} detailed object(s) mapped to ${aggregatedByElementId.size} source iTwin element(s).`,
        ];
        if (updatedElementIds.length > 0) {
          summaryParts.push(
            `Export updated ${updatedElementIds.length} iTwin element(s): ${updatedElementIds.join(", ")}.`
          );
        }
        if (failedMessages.length > 0) {
          summaryParts.push(`Issues: ${failedMessages.join(" ")}`);
        }
        if (!updatedElementIds.length && !failedMessages.length) {
          summaryParts.push("No aspect properties were updated.");
        }

        setMatchSummary(summaryParts.join(" "));
        setMatchLines([...lines, ...skippedMessages]);

        console.info("[iTwin Export] Trimble field-input writeback", {
          selectedLeafIds,
          options,
          selectionCount: selectionDetails.selectedCount,
          selectedTrimbleObjects: selectionDetails.objects.length,
          mappedElementIds: [...aggregatedByElementId.keys()],
          updatedElementIds,
          failedMessages,
          skippedMessages,
          lines,
        });
      } catch (err) {
        console.error("[iTwin Export] Trimble field-input writeback failed:", err);
        setMatchSummary((err as Error).message ?? "Trimble field-input export failed.");
      }
    },
    [iModelConnection, trimbleViewer]
  );

  return (
    <FeatureFilterPanel
      onSelectionChange={(selectedLeafIds) => {
        void trimbleSelectionSync.handleSelectionChange(selectedLeafIds);
      }}
      onActionClick={handleExportClick}
      actionButtonLabel="Export"
      hideDefaultOptions
      statusContent={
        trimbleSelectionSync.summary || matchSummary ? (
          <div style={{ display: "grid", gap: 4 }}>
            {trimbleSelectionSync.summary ? (
              <>
                <div style={{ fontWeight: 600 }}>{trimbleSelectionSync.summary}</div>
                {trimbleSelectionSync.lines.map((line, index) => (
                  <div key={`trimble-sync-${index}-${line}`} style={{ color: "#555" }}>
                    {line}
                  </div>
                ))}
              </>
            ) : null}
            {matchSummary ? (
              <>
                <div style={{ fontWeight: 600 }}>{matchSummary}</div>
                {matchLines.map((line, index) => (
                  <div key={`${index}-${line}`} style={{ color: "#555" }}>
                    {line}
                  </div>
                ))}
              </>
            ) : null}
          </div>
        ) : null
      }
    />
  );
};

export const FieldInputWidget: React.FC = () => {
  const trimble = useTrimbleConnect();
  const trimbleViewer = trimble.api?.viewer;
  const [formState, setFormState] = useState<FieldInputFormState>(() =>
    readStoredFieldInputDraft()
  );
  const [submitSummary, setSubmitSummary] = useState<string | null>(null);
  const [submitLines, setSubmitLines] = useState<string[]>([]);
  const [submitDebugJson, setSubmitDebugJson] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    persistFieldInputDraft(formState);
  }, [formState]);

  const handleSubmit = useCallback(async () => {
    setSubmitSummary("Preparing the selected Trimble IFC model(s) for Field Input writeback...");
    setSubmitLines([]);
    setSubmitDebugJson(null);

    if (!trimbleViewer) {
      setSubmitSummary("Trimble viewer API is not available in this session.");
      return;
    }

    try {
      const accessToken = await trimble.ensureAccessToken();
      if (!accessToken) {
        setSubmitSummary("Trimble access token is unavailable.");
        return;
      }

      const selectionDetails = await getTrimbleSelectionDetails(trimbleViewer);
      if (!selectionDetails.selectedCount) {
        setSubmitSummary("No Trimble objects are currently selected.");
        setSubmitDebugJson(
          JSON.stringify(
            {
              models: [],
              selectedCount: 0,
              selection: selectionDetails.selection.map((entry) => ({
                modelId: entry.modelId,
                objectRuntimeIds: [...(entry.objectRuntimeIds ?? [])],
              })),
              submittedAt: new Date().toISOString(),
              values: parseFieldInputValues(formState),
            } satisfies FieldInputDebugSnapshot,
            null,
            2
          )
        );
        return;
      }

      const values = parseFieldInputValues(formState);
      const debugSnapshot: FieldInputDebugSnapshot = {
        models: [],
        selectedCount: selectionDetails.selectedCount,
        selection: selectionDetails.selection.map((entry) => ({
          modelId: entry.modelId,
          objectRuntimeIds: [...(entry.objectRuntimeIds ?? [])],
        })),
        submittedAt: new Date().toISOString(),
        values,
      };
      const selectedByModel = new Map<
        string,
        {
          objects: TrimbleSelectionObject[];
          runtimeIds: number[];
        }
      >();

      for (const entry of selectionDetails.selection) {
        const existing = selectedByModel.get(entry.modelId);
        const runtimeIds = [...(entry.objectRuntimeIds ?? [])];
        if (!existing) {
          selectedByModel.set(entry.modelId, { runtimeIds, objects: [] });
        } else {
          existing.runtimeIds.push(...runtimeIds);
        }
      }

      for (const object of selectionDetails.objects) {
        const existing = selectedByModel.get(object.modelId);
        if (!existing) continue;
        existing.objects.push(object);
      }

      const lines: string[] = [];
      let patchedModelCount = 0;
      setIsSubmitting(true);

      for (const [modelId, entry] of selectedByModel.entries()) {
        const uniqueRuntimeIds = Array.from(new Set(entry.runtimeIds));
        const debugModel: FieldInputDebugModel = {
          entityIdHints: Array.from(
            new Set(
              entry.objects.flatMap((selectionObject) =>
                collectTrimbleMatchHints(selectionObject.object).entityIdHints
              )
            )
          ),
          externalObjectIds: [],
          guidHints: Array.from(
            new Set(
              entry.objects.flatMap((selectionObject) =>
                collectTrimbleMatchHints(selectionObject.object).guidHints
              )
            )
          ),
          missingEntityIds: [],
          missingObjectIds: [],
          missingSourceElementIds: [],
          modelId,
          normalizedObjectIds: [],
          rawObjectIds: [],
          runtimeIds: uniqueRuntimeIds,
          selectedObjects: entry.objects.map((selectionObject) =>
            buildFieldInputDebugObject(selectionObject)
          ),
          sourceElementIdHints: Array.from(
            new Set(
              entry.objects
                .map((selectionObject) => getTrimbleSourceElementId(selectionObject.object))
                .filter((value) => value.length > 0)
            )
          ),
        };
        debugSnapshot.models.push(debugModel);

        try {
          const externalObjectIds = await trimbleViewer.convertToObjectIds(
            modelId,
            uniqueRuntimeIds
          );
          debugModel.externalObjectIds = (externalObjectIds ?? [])
            .map((value: unknown) => String(value ?? "").trim())
            .filter((value: string) => value.length > 0);

          const guidHints = entry.objects.flatMap((selectionObject) =>
            collectTrimbleMatchHints(selectionObject.object).guidHints
          );
          const entityIdHints = entry.objects.flatMap((selectionObject) =>
            collectTrimbleMatchHints(selectionObject.object).entityIdHints
          );
          const sourceElementIdHints = entry.objects
            .map((selectionObject) => getTrimbleSourceElementId(selectionObject.object))
            .filter((value) => value.length > 0);
          const rawObjectIds = Array.from(
            new Set(
              [...(externalObjectIds ?? []), ...guidHints]
                .map((value) => String(value ?? "").trim())
                .filter((value) => value.length > 0)
            )
          );
          debugModel.rawObjectIds = rawObjectIds;
          debugModel.normalizedObjectIds = rawObjectIds
            .map((value) => normalizeIfcObjectId(value))
            .filter((value): value is string => Boolean(value));

          const loadedModel = await trimbleViewer.getLoadedModel(modelId);
          const filename =
            String(loadedModel?.name ?? loadedModel?.file?.name ?? "").trim() || "trimble.ifc";
          debugModel.filename = filename;
          if (!filename.toLowerCase().endsWith(".ifc")) {
            lines.push(`${filename}: skipped because the loaded Trimble model is not an IFC file.`);
            continue;
          }

          const folderId = String(loadedModel?.file?.parentId ?? "").trim();
          debugModel.folderId = folderId;
          if (!folderId) {
            lines.push(`${filename}: skipped because the Trimble folder id was unavailable.`);
            continue;
          }

          const ifcText = await readTrimbleLoadedModelText(loadedModel?.blob);
          debugModel.ifcTextDebug = buildIfcTextDebugSnapshot({
            entityIdHints,
            ifcText,
            sourceElementIdHints,
          });
          const patchedIfc = patchIfcWithFieldInput({
            entityIdHints,
            ifcText,
            rawObjectIds,
            sourceElementIdHints,
            values,
          });
          debugModel.missingEntityIds = [...patchedIfc.missingEntityIds];
          debugModel.matchedIfcEntityCount = patchedIfc.matchedCount;
          debugModel.missingObjectIds = [...patchedIfc.missingObjectIds];
          debugModel.missingSourceElementIds = [...patchedIfc.missingSourceElementIds];

          if (!patchedIfc.matchedCount) {
            lines.push(
              `${filename}: no matching IFC entities were found for the current Trimble selection.`
            );
            continue;
          }

          const updatedBlob = new Blob([patchedIfc.updatedIfcText], {
            type: "application/x-step",
          });

          await uploadFileToFolder({
            accessToken,
            projectId:
              String(loadedModel?.file?.projectId ?? "").trim() ||
              String(trimble.project?.id ?? "").trim() ||
              undefined,
            folderId,
            blob: updatedBlob,
            filename,
          });

          patchedModelCount += 1;
          lines.push(
            `${filename}: wrote Field Input props to ${patchedIfc.matchedCount} IFC object(s) and uploaded the updated IFC back to Trimble.`
          );
          if (patchedIfc.missingObjectIds.length > 0) {
            lines.push(
              `${filename}: ${patchedIfc.missingObjectIds.length} selected object id(s) did not resolve to IFC entities in the file.`
            );
          }
        } catch (modelErr) {
          debugModel.error =
            (modelErr as Error).message ?? "Field Input upload failed for this Trimble model.";
          lines.push(
            `${modelId}: ${(modelErr as Error).message ?? "Field Input upload failed for this Trimble model."}`
          );
        }
      }

      setSubmitSummary(
        patchedModelCount > 0
          ? `Field Input updated ${patchedModelCount} Trimble IFC model(s). Reload the IFC in Trimble Connect if the new properties do not appear immediately.`
          : "Field Input did not update any Trimble IFC models."
      );
      setSubmitLines(lines);
      setSubmitDebugJson(JSON.stringify(debugSnapshot, null, 2));
    } catch (err) {
      setSubmitSummary((err as Error).message ?? "Field Input submit failed.");
      setSubmitLines([]);
      setSubmitDebugJson(
        JSON.stringify(
          {
            error: (err as Error).message ?? "Field Input submit failed.",
            submittedAt: new Date().toISOString(),
          },
          null,
          2
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [formState, trimble, trimbleViewer]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: 12,
        gap: 10,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14 }}>Field Input</div>
      <div style={{ fontSize: 12, color: "#475467", lineHeight: 1.45 }}>
        Submit applies to the objects currently selected in Trimble Connect. Use the Import or
        Export feature filters first if you want to target a full category like Lighting or Signals.
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="checkbox"
          checked={formState.flagDesign}
          onChange={(event) =>
            setFormState((current) => ({
              ...current,
              flagDesign: Boolean(event.target.checked),
            }))
          }
        />
        <span>Flag Design</span>
      </label>

      <label style={{ display: "grid", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Notes</span>
        <textarea
          value={formState.notes}
          onChange={(event) =>
            setFormState((current) => ({
              ...current,
              notes: event.target.value,
            }))
          }
          rows={4}
          placeholder="Write temporary NiceConnect notes into the selected Trimble IFC object(s)"
          style={{
            width: "100%",
            resize: "vertical",
            fontFamily: "inherit",
            fontSize: 12,
            padding: 8,
            border: "1px solid #c8ccd0",
            borderRadius: 4,
          }}
        />
      </label>

      <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>X</span>
          <input
            type="number"
            step="any"
            value={formState.asBuiltX}
            onChange={(event) =>
              setFormState((current) => ({
                ...current,
                asBuiltX: event.target.value,
              }))
            }
            style={{
              width: "100%",
              fontSize: 12,
              padding: 8,
              border: "1px solid #c8ccd0",
              borderRadius: 4,
            }}
          />
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Y</span>
          <input
            type="number"
            step="any"
            value={formState.asBuiltY}
            onChange={(event) =>
              setFormState((current) => ({
                ...current,
                asBuiltY: event.target.value,
              }))
            }
            style={{
              width: "100%",
              fontSize: 12,
              padding: 8,
              border: "1px solid #c8ccd0",
              borderRadius: 4,
            }}
          />
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Z</span>
          <input
            type="number"
            step="any"
            value={formState.asBuiltZ}
            onChange={(event) =>
              setFormState((current) => ({
                ...current,
                asBuiltZ: event.target.value,
              }))
            }
            style={{
              width: "100%",
              fontSize: 12,
              padding: 8,
              border: "1px solid #c8ccd0",
              borderRadius: 4,
            }}
          />
        </label>
      </div>

      <div style={{ marginTop: "auto", display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={isSubmitting}
          style={{
            padding: "6px 12px",
            borderRadius: 4,
            border: "1px solid #0f62fe",
            background: isSubmitting ? "#d0d5dd" : "#0f62fe",
            color: "#fff",
            cursor: isSubmitting ? "default" : "pointer",
          }}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </div>

      {(submitSummary || submitLines.length > 0) && (
        <div style={{ fontSize: 12, lineHeight: 1.45, display: "grid", gap: 4 }}>
          {submitSummary ? <div style={{ fontWeight: 600 }}>{submitSummary}</div> : null}
          {submitLines.map((line, index) => (
            <div key={`field-input-${index}-${line}`} style={{ color: "#555" }}>
              {line}
            </div>
          ))}
        </div>
      )}

      {submitDebugJson ? (
        <details open style={{ fontSize: 12, display: "grid", gap: 6 }}>
          <summary style={{ cursor: "pointer", fontWeight: 600 }}>Selection Debug</summary>
          <pre
            style={{
              margin: 0,
              padding: 10,
              borderRadius: 4,
              border: "1px solid #d0d5dd",
              background: "#f8f9fb",
              color: "#344054",
              overflow: "auto",
              maxHeight: 280,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {submitDebugJson}
          </pre>
        </details>
      ) : null}
    </div>
  );
};

export const featureSelectionUiProvider = {
  // Version the provider/widget ids so AppUI doesn't keep stale tab content
  // around while we're iterating on this temporary demo panel setup.
  id: "FeatureSelectionUiProviderV3",
  getWidgets: () => {
    const widgets = [
      {
        id: "FeatureSelectionImportWidgetV3",
        label: "Import",
        content: <FeatureSelectionWidget />,
        layouts: {
          standard: {
            location: StagePanelLocation.Right,
            section: StagePanelSection.Start,
          },
        },
      },
      {
        id: "FeatureExportWidgetV3",
        label: "Export",
        content: <FeatureExportWidget />,
        layouts: {
          standard: {
            location: StagePanelLocation.Right,
            section: StagePanelSection.Start,
          },
        },
      },
    ];

    if (shouldShowMonitoringAdminWidget()) {
      widgets.push({
        id: "MonitoringAdminWidget",
        label: "Admin",
        content: <MonitoringAdminWidget />,
        layouts: {
          standard: {
            location: StagePanelLocation.Right,
            section: StagePanelSection.Start,
          },
        },
      });
    }

    return widgets;
  },
};

